import React, { useState, useEffect, useRef, useMemo } from 'react';
import styled, { keyframes, css, useTheme } from 'styled-components';
import {
  Eye,
  X,
  LogOut,
  RefreshCw,
  ScanLine,
  ShoppingCart,
  Users,
  Package as PackageIcon,
  Boxes,
  ShoppingBag,
  Printer,
  LayoutDashboard,
  TrendingUp,
  DollarSign,
  Truck,
  Glasses,
  FileText,
  Search,
  ClipboardList,
  Star,
} from 'lucide-react';
import axios from 'axios';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { hasTask, hasAnyTask } from '../../utils/tasks';
import { printReceiptHtml, printEquipmentPassport, hasTrialLens, TRIAL_LENS_EXTRA_DAYS } from '../../utils/receiptDownload';
import { clearSalesFlowSession } from '../../utils/salesSession';
import { useBranding, DEFAULT_BRANDING } from '../../contexts/BrandingContext';
import ClientList from '../../components/ClientList';
import LensStockPage from '../LensStock/LensStockPage';
import FrameStockPage from '../FrameStock/FrameStockPage';
import SupplyStockPage from '../SupplyStock/SupplyStockPage';
import PeerRatingPage from '../PeerRating/PeerRatingPage';
import PackageScanner from '../../components/PackageScanner';
import { buildPackageLabelHtml, openPrintIframe } from '../../utils/packageLabel';
import ReceiveChecklist from '../../components/ReceiveChecklist';
import ThirdPartyHandoffModal from '../../components/ThirdPartyHandoffModal/ThirdPartyHandoffModal';
import { useTranslation } from 'react-i18next';

/** Translate order lifecycle / status codes (CONFIRMED, created_not_confirmed, …). */
const tOrderStatus = (t, stage) => {
  if (!stage) return t('manager.unknown');
  const key = String(stage).toLowerCase().replace(/\s+/g, '_');
  const translated = t(`orderStatus.${key}`, { defaultValue: '' });
  return translated || String(stage).replace(/_/g, ' ');
};

/** Translate payment status codes (pending, paid, UNPAID, …). */
const tPaymentStatus = (t, status) => {
  const key = String(status || 'unpaid').toLowerCase().replace(/\s+/g, '_');
  return t(`paymentStatus.${key}`, { defaultValue: status || t('paymentStatus.unpaid') });
};

/** Translate visitor gender + age group for order cards. */
const tVisitorDemo = (t, gender, ageGroup) => {
  const g = gender
    ? t(`visitor.${String(gender).toLowerCase()}`, { defaultValue: gender })
    : t('manager.visitor');
  const a = ageGroup
    ? t(`visitor.${String(ageGroup).toLowerCase()}`, { defaultValue: ageGroup })
    : t('manager.na');
  return `${g} · ${a}`;
};

/** Translate package / stock-request status codes. */
const tPackageStatus = (t, status) => {
  if (!status) return t('manager.unknown');
  const key = String(status).toLowerCase().replace(/\s+/g, '_');
  const translated = t(`packageStatus.${key}`, { defaultValue: '' });
  return translated || String(status).replace(/_/g, ' ');
};

/** Translate product type shown on stock requests (lens, frame, …). */
const tProductType = (t, type) => {
  if (!type) return '';
  const key = String(type).toLowerCase();
  return t(`packageLabel.${key}`, { defaultValue: type });
};

// ─── Animation Keyframes ───────────────────────────────────────────
const fadeEntrance = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const pulseRing = keyframes`
  0%   { transform: scale(1);   opacity: 0.9; }
  70%  { transform: scale(2.2); opacity: 0; }
  100% { transform: scale(1);   opacity: 0; }
`;

const shimmerMove = keyframes`
  0%   { background-position: -200% center; }
  100% { background-position:  200% center; }
`;

// ─── Custom Hooks ──────────────────────────────────────────────────
const useCountUp = (target, duration = 950) => {
  const [val, setVal] = useState(0);
  const raf = useRef(null);
  const prev = useRef(target);

  useEffect(() => {
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduced || !target) { setVal(target ?? 0); return; }

    prev.current = target;
    setVal(0);
    const t0 = performance.now();

    const tick = (now) => {
      if (prev.current !== target) return;
      const p = Math.min((now - t0) / duration, 1);
      setVal(Math.round(target * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf.current = requestAnimationFrame(tick);
      else setVal(target);
    };

    raf.current = requestAnimationFrame(tick);
    return () => raf.current && cancelAnimationFrame(raf.current);
  }, [target, duration]);

  return val;
};

// ─── Sidebar ───────────────────────────────────────────────────────
// The sidebar is the one dark surface in the app, so it can't just use
// theme.colors.primary directly — it needs a whole dark palette. App.js sets
// primary/primaryDark/primaryLight to the *same* branding colour, so any
// gradient built from those is flat. We derive the shades here instead: take
// the brand hue, keep a damped version of its saturation, and pin the
// lightness per role. A red store gets a near-black red sidebar, a green one
// gets the original green, and both keep the same contrast ratios.
const hexToHsl = (hex) => {
  const m = /^#?([a-f\d]{3}|[a-f\d]{6})$/i.exec(String(hex || '').trim());
  if (!m) return null;
  let c = m[1];
  if (c.length === 3) c = c[0] + c[0] + c[1] + c[1] + c[2] + c[2];
  const r = parseInt(c.slice(0, 2), 16) / 255;
  const g = parseInt(c.slice(2, 4), 16) / 255;
  const b = parseInt(c.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  const d = max - min;
  if (!d) return { h: 0, s: 0, l: l * 100 };
  const s = d / (1 - Math.abs(2 * l - 1));
  let h;
  if (max === r) h = ((g - b) / d) % 6;
  else if (max === g) h = (b - r) / d + 2;
  else h = (r - g) / d + 4;
  h = Math.round(h * 60);
  if (h < 0) h += 360;
  return { h, s: s * 100, l: l * 100 };
};

const PALETTE_CACHE = new Map();

const sidebarPalette = (hex) => {
  const key = hex || 'default';
  const cached = PALETTE_CACHE.get(key);
  if (cached) return cached;

  const hsl = hexToHsl(hex) || { h: 160, s: 84 };
  const { h } = hsl;
  // Damp the brand saturation, then clamp: a neon brand must not produce a
  // vibrating sidebar, and a near-grey one must not produce a dead flat slab.
  const sat = (mult) => Math.max(10, Math.min(58, Math.round(hsl.s * mult)));
  const c = (s, l) => `hsl(${h} ${sat(s)}% ${l}%)`;

  const p = {
    bgTop:      c(0.55, 5.5),
    bgMid:      c(0.48, 8.5),
    bgBottom:   c(0.50, 7),
    brandName:  c(0.80, 94),   // store name in the header
    brandSub:   c(0.40, 32),   // "Manager Panel"
    navLabel:   c(0.40, 27),   // the MENU caption
    navIdle:    c(0.30, 45),
    navHover:   c(0.42, 72),
    userName:   c(0.70, 92),
    userRole:   c(0.32, 41),
    // A darker companion to the brand colour, so gradients built from
    // primary -> primaryDark still read as gradients when App.js has set
    // both to the same hex.
    brandDeep:  `hsl(${h} ${Math.round(Math.min(hsl.s, 85))}% ${Math.max(14, Math.round((hsl.l ?? 40) * 0.62))}%)`,
  };
  PALETTE_CACHE.set(key, p);
  return p;
};

/** Shorthand for interpolations: pal(p).navIdle */
const pal = (p) => sidebarPalette(p.theme.colors.primary);

const Sidebar = styled.aside`
  width: 264px;
  min-height: 100vh;
  max-height: 100vh;
  position: sticky;
  top: 0;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  background: linear-gradient(180deg,
    ${p => pal(p).bgTop} 0%,
    ${p => pal(p).bgMid} 55%,
    ${p => pal(p).bgBottom} 100%);
  box-shadow: 4px 0 32px rgba(0, 0, 0, 0.2);
  z-index: 20;
`;

const SidebarHeader = styled.div`
  padding: 1.125rem 0.875rem 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  flex-shrink: 0;
`;

const LogoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.625rem;
  min-width: 0;
`;

const LogoMark = styled.div`
  width: 40px;
  height: 40px;
  background: ${p => p.hasImage
    ? 'rgba(255, 255, 255, 0.96)'
    : `linear-gradient(135deg, ${p.theme.colors.primary} 0%, ${pal(p).brandDeep} 100%)`};
  border-radius: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;
  box-shadow: ${p => p.hasImage
    ? '0 4px 14px rgba(0, 0, 0, 0.35), 0 0 0 1px rgba(255, 255, 255, 0.12)'
    : `0 4px 16px ${p.theme.colors.primary}80, 0 0 0 1px ${p.theme.colors.primary}4D`};

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    padding: 3px;
  }
`;

const LogoText = styled.div`
  min-width: 0;
  flex: 1;

  h2 {
    margin: 0;
    font-size: 0.9375rem;
    font-weight: 700;
    color: ${p => pal(p).brandName};
    line-height: 1.2;
    letter-spacing: -0.01em;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  p  {
    margin: 1px 0 0;
    font-size: 0.6875rem;
    color: ${p => pal(p).brandSub};
    line-height: 1.4;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const SidebarNav = styled.nav`
  padding: 0.75rem;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 2px;

  /* Thin, dark scrollbar so a long nav never draws a bright bar. */
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.12) transparent;
  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 3px;
  }
  &::-webkit-scrollbar-thumb:hover { background: rgba(255, 255, 255, 0.18); }
`;

const NavLabel = styled.div`
  padding: 0.25rem 0.75rem 0.5rem;
  font-size: 0.625rem;
  font-weight: 700;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: ${p => pal(p).navLabel};
`;

const NavItem = styled.button`
  position: relative;
  width: 100%;
  padding: 0.5625rem 0.75rem;
  cursor: pointer;
  border: 1px solid ${p => p.active ? `${p.theme.colors.primary}38` : 'transparent'};
  border-radius: 10px;
  transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);
  display: flex;
  align-items: center;
  gap: 0.625rem;
  font-weight: ${p => p.active ? '600' : '500'};
  font-size: 0.875rem;
  font-family: inherit;
  text-align: left;
  background: ${p => p.active
    ? `linear-gradient(135deg, ${p.theme.colors.primary}29 0%, ${pal(p).brandDeep}1A 100%)`
    : 'transparent'};
  color: ${p => p.active ? p.theme.colors.primary : pal(p).navIdle};
  box-shadow: ${p => p.active
    ? `0 0 20px ${p.theme.colors.primary}1A, inset 0 1px 0 rgba(255,255,255,0.04)`
    : 'none'};

  /* Accent rail on the active row. */
  &::before {
    content: '';
    position: absolute;
    left: -1px;
    top: 50%;
    transform: translateY(-50%);
    width: 3px;
    height: ${p => p.active ? '18px' : '0'};
    border-radius: 0 3px 3px 0;
    background: ${p => p.theme.colors.primary};
    box-shadow: 0 0 10px ${p => p.theme.colors.primary}99;
    transition: height 0.22s cubic-bezier(0.16, 1, 0.3, 1);
  }

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      background: ${p => p.active
        ? `linear-gradient(135deg, ${p.theme.colors.primary}33 0%, ${pal(p).brandDeep}24 100%)`
        : 'rgba(255, 255, 255, 0.04)'};
      color: ${p => p.active ? p.theme.colors.primary : pal(p).navHover};
      transform: translateX(2px);
    }
  }

  &:active { transform: scale(0.97); }
  &:focus-visible {
    outline: 2px solid ${p => p.theme.colors.primary}80;
    outline-offset: 1px;
  }
  svg { flex-shrink: 0; }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
    &::before { transition: none; }
    &:active { transform: none; }
  }
`;

const NavDivider = styled.div`
  height: 1px;
  margin: 0.5rem;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.07), transparent);
`;

const NavDot = styled.span`
  width: 6px;
  height: 6px;
  background: ${p => p.theme.colors.primary};
  border-radius: 50%;
  margin-left: auto;
  flex-shrink: 0;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    inset: -4px;
    border-radius: 50%;
    background: ${p => p.theme.colors.primary}73;
    animation: ${pulseRing} 2.2s ease-out infinite;
  }

  @media (prefers-reduced-motion: reduce) { &::before { display: none; } }
`;

const SidebarFooter = styled.div`
  padding: 0.625rem 0.75rem 0.75rem;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
  flex-shrink: 0;
`;

/* Who is signed in — avatar, name, role, employee id. Sits directly under the
   store logo so the two identities the panel acts on (which store, which
   employee) read as one block. */
const UserCard = styled.div`
  margin-top: 0.875rem;
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.5rem;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.035);
  border: 1px solid rgba(255, 255, 255, 0.05);
  min-width: 0;
`;

const UserAvatar = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  flex-shrink: 0;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${p => p.hasImage
    ? 'rgba(255, 255, 255, 0.06)'
    : `linear-gradient(135deg, ${p.theme.colors.primary} 0%, ${pal(p).brandDeep} 100%)`};
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.08);

  img { width: 100%; height: 100%; object-fit: cover; }

  .initials {
    font-size: 0.8125rem;
    font-weight: 700;
    color: #fff;
    letter-spacing: 0.02em;
  }
`;

const UserMeta = styled.div`
  min-width: 0;
  flex: 1;

  .name {
    font-size: 0.8125rem;
    font-weight: 600;
    color: ${p => pal(p).userName};
    line-height: 1.25;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .role {
    margin-top: 2px;
    font-size: 0.6875rem;
    color: ${p => pal(p).userRole};
    line-height: 1.3;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .role b {
    font-weight: 600;
    color: ${p => p.theme.colors.primary};
    opacity: 0.85;
  }
`;

const LogoutButton = styled.button`
  width: 100%;
  padding: 0.5rem 0.75rem;
  cursor: pointer;
  border: 1px solid transparent;
  border-radius: 10px;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.625rem;
  color: #7a5252;
  background: transparent;
  font-weight: 500;
  font-size: 0.8125rem;
  font-family: inherit;
  text-align: left;

  &:hover {
    background: rgba(239, 68, 68, 0.1);
    border-color: rgba(239, 68, 68, 0.2);
    color: #fca5a5;
  }

  &:focus-visible {
    outline: 2px solid rgba(239, 68, 68, 0.5);
    outline-offset: 1px;
  }

  svg { flex-shrink: 0; }

  @media (prefers-reduced-motion: reduce) { transition: none; }
`;

// ─── Layout ────────────────────────────────────────────────────────
const Container = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #f0fdf4 0%, #f8fafc 60%, #eff6ff 100%);
  display: flex;
`;

const MainContent = styled.div`
  flex: 1;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const PageHeader = styled.header`
  background: rgba(255, 255, 255, 0.82);
  backdrop-filter: blur(24px);
  border-bottom: 1px solid ${p => p.theme.colors.primary}14;
  padding: 0.875rem 1.75rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  position: sticky;
  top: 0;
  z-index: 10;
  box-shadow: 0 1px 16px rgba(0, 0, 0, 0.04);
`;

const PageTitleGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const Title = styled.h1`
  font-size: 1.5rem;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
  letter-spacing: -0.02em;
`;

const LiveBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.6875rem;
  font-weight: 700;
  color: ${p => p.theme.colors.primary};
  background: ${p => p.theme.colors.primary}14;
  border: 1px solid ${p => p.theme.colors.primary}66;
  border-radius: 20px;
  padding: 4px 10px;
  letter-spacing: 0.02em;
`;

const LiveDot = styled.span`
  display: inline-block;
  width: 6px;
  height: 6px;
  background: ${p => p.theme.colors.primary};
  border-radius: 50%;
  position: relative;
  flex-shrink: 0;

  &::before {
    content: '';
    position: absolute;
    inset: -4px;
    border-radius: 50%;
    background: ${p => p.theme.colors.primary}80;
    animation: ${pulseRing} 2s ease-out infinite;
  }

  @media (prefers-reduced-motion: reduce) { &::before { display: none; } }
`;

const ContentArea = styled.div`
  padding: 1.5rem 1.75rem;
  flex: 1;
  animation: ${fadeEntrance} 320ms cubic-bezier(0.23, 1, 0.32, 1) both;

  @media (prefers-reduced-motion: reduce) { animation: none; }
`;

// ─── Buttons ───────────────────────────────────────────────────────
const AddButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4375rem;
  padding: 8px 16px;
  background: ${p => p.theme.colors.primary};
  color: white;
  border: none;
  border-radius: 10px;
  font-weight: 600;
  font-size: 0.875rem;
  font-family: inherit;
  cursor: pointer;
  transition: background 150ms ease, transform 160ms ease-out;
  white-space: nowrap;

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      background: ${p => p.theme.colors.primaryDark};
      transform: translateY(-1px);
    }
  }

  &:active { transform: scale(0.97); }
  svg { flex-shrink: 0; }

  @media (prefers-reduced-motion: reduce) { transition: none; &:active { transform: none; } }
`;

const ErrorMessage = styled.div`
  background: linear-gradient(135deg, #fef2f2, #fde8e8);
  color: #dc2626;
  border: 1px solid #fecaca;
  padding: 0.75rem 1rem;
  border-radius: 12px;
  margin-bottom: 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  animation: ${slideUp} 0.25s ease;
`;

// ─── Glass Card ────────────────────────────────────────────────────
const Card = styled.div`
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06), 0 8px 24px rgba(0, 0, 0, 0.06);
  transition: transform 200ms cubic-bezier(0.23, 1, 0.32, 1), box-shadow 200ms ease;

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      transform: translateY(-3px);
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.07), 0 16px 32px rgba(0, 0, 0, 0.1);
    }
  }

  @media (prefers-reduced-motion: reduce) { transition: none; }
`;

// ─── Modal ─────────────────────────────────────────────────────────
const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.52);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
  backdrop-filter: blur(6px);
  animation: ${fadeIn} 0.18s ease;
`;

const ModalContent = styled.div`
  background: #ffffff;
  border-radius: 16px;
  width: 100%;
  max-width: 580px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.07), 0 20px 48px rgba(0, 0, 0, 0.14);
  animation: ${slideUp} 0.3s cubic-bezier(0.23, 1, 0.32, 1);

  @media (prefers-reduced-motion: reduce) { animation: none; }
`;

const ModalHeader = styled.div`
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid ${p => p.theme.colors.primary}14;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ModalTitle = styled.h2`
  font-size: 1.0625rem;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
  letter-spacing: -0.01em;
`;

const CloseButton = styled.button`
  width: 32px;
  height: 32px;
  border: none;
  background: #f1f5f9;
  cursor: pointer;
  border-radius: 8px;
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  flex-shrink: 0;

  &:hover { background: #e2e8f0; color: #0f172a; transform: rotate(90deg); }

  @media (prefers-reduced-motion: reduce) { transition: none; &:hover { transform: none; } }
`;

const ModalBody = styled.div`
  padding: 1.5rem;
`;

// ─── Form Elements ─────────────────────────────────────────────────
const FormGroup = styled.div`
  margin-bottom: 1.125rem;
`;

const Label = styled.label`
  display: block;
  font-weight: 600;
  color: #334155;
  margin-bottom: 0.375rem;
  font-size: 0.8125rem;
`;

const inputBase = css`
  width: 100%;
  padding: 0.625rem 0.875rem;
  border: 1.5px solid #e2e8f0;
  border-radius: 10px;
  font-size: 0.875rem;
  font-family: inherit;
  color: #0f172a;
  background: rgba(248, 250, 252, 0.8);
  transition: border-color 0.15s, box-shadow 0.15s, background 0.15s;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${p => p.theme.colors.primary};
    background: white;
    box-shadow: 0 0 0 3px ${p => p.theme.colors.primary}1A;
  }

  &::placeholder { color: #cbd5e1; }
`;

const Input    = styled.input`${inputBase}`;
const Select   = styled.select`${inputBase} cursor: pointer;`;
const Textarea = styled.textarea`${inputBase} min-height: 96px; resize: vertical; line-height: 1.5;`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: 1.5rem;
  justify-content: flex-end;
`;

const Button = styled.button`
  padding: 8px 16px;
  border: none;
  border-radius: 10px;
  font-weight: 600;
  font-size: 0.875rem;
  font-family: inherit;
  cursor: pointer;

  ${p => p.primary ? css`
    background: ${p.theme.colors.primary};
    color: white;
    transition: background 150ms ease, transform 160ms ease-out;
    @media (hover: hover) and (pointer: fine) {
      &:hover { background: ${p.theme.colors.primaryDark}; transform: translateY(-1px); }
    }
    &:active { transform: scale(0.97); }
    &:disabled { opacity: 0.5; cursor: not-allowed; transform: none; }
  ` : css`
    background: white;
    color: #64748b;
    border: 1.5px solid #e2e8f0;
    transition: border-color 150ms ease, color 150ms ease, transform 160ms ease-out;
    @media (hover: hover) and (pointer: fine) {
      &:hover { border-color: ${p.theme.colors.primary}; color: ${p.theme.colors.primary}; }
    }
    &:active { transform: scale(0.97); }
  `}

  @media (prefers-reduced-motion: reduce) { transition: none; &:active { transform: none; } }
`;

// ─── Status Badges ─────────────────────────────────────────────────
const STATUS_CFG = {
  CREATED_NOT_CONFIRMED: { bg: 'linear-gradient(135deg,#fef2f2,#ffe4e4)', color: '#dc2626', border: '#fecaca' },
  CONFIRMED:             { bg: 'linear-gradient(135deg,#eff6ff,#dbeafe)', color: '#2563eb', border: '#bfdbfe' },
  MANUFACTURING:         { bg: 'linear-gradient(135deg,#faf5ff,#ede9fe)', color: '#7c3aed', border: '#ddd6fe' },
  READY_FOR_PICKUP:      { bg: 'linear-gradient(135deg,#f0fdf4,#dcfce7)', color: '#16a34a', border: '#bbf7d0' },
  DELIVERED_TO_CLIENT:   { bg: 'linear-gradient(135deg,#f0fdf4,#dcfce7)', color: '#16a34a', border: '#bbf7d0' },
  COMPLETED:             { bg: 'linear-gradient(135deg,#ecfdf5,#d1fae5)', color: '#065f46', border: '#6ee7b7' },
  pending:               { bg: 'linear-gradient(135deg,#fffbeb,#fef3c7)', color: '#d97706', border: '#fde68a' },
};

const StatusBadge = styled.span`
  padding: 3px 10px;
  border-radius: 6px;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  white-space: nowrap;
  border: 1px solid ${p => (STATUS_CFG[p.status] || { border: '#e2e8f0' }).border};
  background: ${p => (STATUS_CFG[p.status] || { bg: '#f8fafc' }).bg};
  color: ${p => (STATUS_CFG[p.status] || { color: '#64748b' }).color};
`;

// ─── Overview KPI Cards ────────────────────────────────────────────
const OverviewGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const OverviewCard = styled.div`
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06), 0 8px 24px rgba(0, 0, 0, 0.06);
  display: flex;
  flex-direction: column;
  min-height: 286px;
  overflow: hidden;
  transition: transform 200ms cubic-bezier(0.23, 1, 0.32, 1), box-shadow 200ms ease;
  animation: ${fadeEntrance} 320ms cubic-bezier(0.23, 1, 0.32, 1) both;
  animation-delay: ${p => (p.$index || 0) * 60}ms;

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      transform: translateY(-3px);
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.07), 0 16px 32px rgba(0, 0, 0, 0.1);
    }
  }

  @media (prefers-reduced-motion: reduce) { animation: none; transition: none; }
`;

const AccentBar = styled.div`
  height: 4px;
  background: ${p => p.gradient};
  flex-shrink: 0;
  position: relative;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(255, 255, 255, 0.65) 50%,
      transparent 100%
    );
    background-size: 200% 100%;
    animation: ${shimmerMove} 2.8s ease-in-out infinite;
  }

  @media (prefers-reduced-motion: reduce) { &::after { display: none; } }
`;

const OverviewCardHead = styled.div`
  padding: 1rem 1.125rem 0;
  display: flex;
  align-items: center;
  justify-content: space-between;

  h3 {
    margin: 0;
    font-size: 0.9rem;
    font-weight: 700;
    color: #0f172a;
    display: flex;
    align-items: center;
    gap: 0.4rem;
    letter-spacing: -0.015em;
  }

  .sub {
    font-size: 0.6875rem;
    color: #94a3b8;
    font-weight: 500;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 9999px;
    padding: 0.125rem 0.5rem;
  }
`;

const OverviewCardBody = styled.div`
  padding: 0.875rem 1.125rem;
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const OverviewCardFoot = styled.div`
  padding: 0.625rem 1.125rem;
  background: linear-gradient(135deg, #f8fffd 0%, ${p => p.theme.colors.primary}0D 100%);
  border-top: 1px solid ${p => p.theme.colors.primary}12;
  font-size: 0.78125rem;
  color: #475569;
  text-align: center;
  strong { color: #0f172a; font-variant-numeric: tabular-nums; }
`;

const ManagerPeerRatingBanner = styled.button`
  width: calc(100% - 1.5rem);
  margin: 0.5rem 0.75rem 0;
  padding: 0.75rem 0.85rem;
  background: linear-gradient(135deg, rgba(251, 191, 36, 0.16) 0%, rgba(245, 158, 11, 0.12) 100%);
  border: 1px solid rgba(251, 191, 36, 0.34);
  border-radius: 10px;
  color: rgba(255, 255, 255, 0.88);
  cursor: pointer;
  text-align: left;
  animation: managerPeerGlow 3s ease-in-out infinite;

  @keyframes managerPeerGlow {
    0%, 100% { box-shadow: 0 0 0 0 rgba(251, 191, 36, 0); }
    50% { box-shadow: 0 0 8px 2px rgba(251, 191, 36, 0.16); }
  }

  .title {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: #fbbf24;
    font-weight: 700;
    font-size: 0.8rem;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    margin-bottom: 0.45rem;
  }

  .note {
    font-size: 0.74rem;
    line-height: 1.35;
    color: rgba(255, 255, 255, 0.72);
  }
`;

const DateRangeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  flex-wrap: wrap;
  margin-bottom: 1rem;

  .preset {
    border: 1.5px solid #e2e8f0;
    border-radius: 8px;
    padding: 6px 14px;
    font-size: 0.75rem;
    font-weight: 600;
    background: #f1f5f9;
    color: #64748b;
    cursor: pointer;
    transition: background 150ms ease, color 150ms ease, border-color 150ms ease;
    font-family: inherit;

    @media (hover: hover) and (pointer: fine) {
      &:hover { background: #e2e8f0; }
    }

    &.active {
      background: ${p => p.theme.colors.primary};
      color: white;
      border-color: ${p => p.theme.colors.primary};
    }

    @media (prefers-reduced-motion: reduce) { transition: none; }
  }
`;

const DatePill = styled.input`
  border: 1.5px solid #e2e8f0;
  border-radius: 8px;
  padding: 6px 14px;
  font-size: 0.75rem;
  font-weight: 600;
  font-family: inherit;
  background: rgba(255, 255, 255, 0.75);
  backdrop-filter: blur(8px);
  color: #475569;
  cursor: pointer;
  transition: all 0.18s cubic-bezier(0.16, 1, 0.3, 1);
  outline: none;
  min-width: 0;

  &:focus {
    border-color: ${p => p.theme.colors.primary};
    background: rgba(255, 255, 255, 0.95);
    box-shadow: 0 0 0 3px ${p => p.theme.colors.primary}1F, 0 2px 8px ${p => p.theme.colors.primary}1A;
    color: #0f172a;
  }

  &:hover:not(:focus) {
    background: rgba(255, 255, 255, 0.92);
    border-color: ${p => p.theme.colors.primary}66;
    color: ${p => p.theme.colors.primaryDark};
    box-shadow: 0 2px 8px ${p => p.theme.colors.primary}1F;
  }

  &::-webkit-calendar-picker-indicator {
    cursor: pointer;
    opacity: 0.5;
    margin-left: 2px;
    filter: invert(0.3) sepia(1) saturate(3) hue-rotate(110deg);
  }

  @media (prefers-reduced-motion: reduce) { transition: none; }
`;

const DonutWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  flex: 1;

  .legend { display: flex; flex-direction: column; gap: 0.2rem; flex: 1; min-width: 0; }

  .legend .item {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    font-size: 0.8125rem;
    color: #475569;
    border-radius: 8px;
    padding: 2px 4px;
    transition: background 150ms ease;
    @media (hover: hover) and (pointer: fine) {
      &:hover { background: #f8fafc; }
    }
  }

  .legend .swatch { width: 10px; height: 10px; border-radius: 3px; flex-shrink: 0; }
  .legend .num { font-weight: 700; color: #0f172a; margin-left: auto; font-variant-numeric: tabular-nums; }
`;

// ─── Orders ────────────────────────────────────────────────────────
const SearchBar = styled.div`
  display: flex;
  gap: 0.625rem;
  margin-bottom: 1rem;
`;

const SearchInputWrap = styled.div`
  flex: 1;
  position: relative;
  svg { position: absolute; left: 0.75rem; top: 50%; transform: translateY(-50%); color: #94a3b8; pointer-events: none; }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 10px 16px 10px 40px;
  border: 1.5px solid #e2e8f0;
  border-radius: 12px;
  font-size: 0.875rem;
  font-family: inherit;
  color: #0f172a;
  background: white;
  transition: border-color 150ms ease, box-shadow 150ms ease;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${p => p.theme.colors.primary};
    box-shadow: 0 0 0 3px ${p => p.theme.colors.primary}1A;
  }

  &::placeholder { color: #cbd5e1; }
`;

const FilterSelect = styled.select`
  padding: 10px 14px;
  border: 1.5px solid #e2e8f0;
  border-radius: 10px;
  font-size: 0.875rem;
  font-family: inherit;
  background: white;
  color: #334155;
  cursor: pointer;
  transition: border-color 150ms ease, box-shadow 150ms ease;
  &:focus {
    outline: none;
    border-color: ${p => p.theme.colors.primary};
    box-shadow: 0 0 0 3px ${p => p.theme.colors.primary}1A;
  }
`;

const SelectionBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
  padding: 0.625rem 0.875rem;
  background: linear-gradient(90deg, ${p => p.theme.colors.primary}14, #eff6ff);
  border: 1px solid ${p => p.theme.colors.primary}66;
  border-radius: 12px;
  font-size: 0.875rem;
  color: #334155;
  margin-bottom: 0.875rem;
`;

const SelectionClear = styled.button`
  margin-left: 0.375rem;
  background: transparent;
  border: none;
  color: ${p => p.theme.colors.accent};
  cursor: pointer;
  font-weight: 600;
  font-size: 0.8125rem;
  font-family: inherit;
  padding: 0;
  &:hover { text-decoration: underline; }
`;

const CombineButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 8px 16px;
  border-radius: 10px;
  border: none;
  background: ${p => p.theme.colors.primary};
  color: white;
  font-weight: 600;
  font-size: 0.875rem;
  font-family: inherit;
  cursor: pointer;
  transition: background 150ms ease, transform 160ms ease-out;

  @media (hover: hover) and (pointer: fine) {
    &:hover:not(:disabled) { background: ${p => p.theme.colors.primaryDark}; transform: translateY(-1px); }
  }
  &:active:not(:disabled) { transform: scale(0.97); }
  &:disabled { opacity: 0.5; cursor: not-allowed; }

  @media (prefers-reduced-motion: reduce) { transition: none; &:active { transform: none; } }
`;

const PackageNotice = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
  padding: 0.625rem 0.875rem;
  border-radius: 12px;
  font-size: 0.875rem;
  margin-bottom: 0.875rem;
  font-weight: 500;
  animation: ${slideUp} 0.25s ease;
  background: ${p => p.kind === 'success'
    ? `linear-gradient(135deg, ${p.theme.colors.primary}14, ${p.theme.colors.primary}0D)`
    : 'linear-gradient(135deg, #fef2f2, #fde8e8)'};
  color: ${p => p.kind === 'success' ? '#166534' : '#991b1b'};
  border: 1px solid ${p => p.kind === 'success' ? `${p.theme.colors.primary}66` : '#fecaca'};

  button {
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
    background: white;
    color: #0f172a;
    border: 1.5px solid #e2e8f0;
    border-radius: 8px;
    padding: 5px 10px;
    cursor: pointer;
    font-weight: 600;
    font-size: 0.75rem;
    font-family: inherit;
    transition: border-color 150ms ease, transform 160ms ease-out;
    @media (hover: hover) and (pointer: fine) {
      &:hover { border-color: ${p => p.theme.colors.primary}; }
    }
    &:active { transform: scale(0.97); }
  }
`;

const OrdersList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
`;

const ORDER_LEFT_BORDER = {
  CONFIRMED:             'accent',
  confirmed:             'accent',
  DELIVERED_TO_CLIENT:   'primary',
  delivered:             'primary',
  READY_FOR_PICKUP:      'primary',
  ready_for_pickup:      'primary',
  COMPLETED:             '#94a3b8',
  completed:             '#94a3b8',
  MANUFACTURING:         '#f59e0b',
  manufacturing:         '#f59e0b',
  CREATED_NOT_CONFIRMED: '#ef4444',
  pending:               '#ef4444',
};

const OrderCard = styled.div`
  border: 1px solid ${p => p.selected ? p.theme.colors.primary : '#f1f5f9'};
  border-left: 4px solid ${p => {
    const v = ORDER_LEFT_BORDER[p.$lifecycle];
    if (v === 'primary') return p.theme.colors.primary;
    if (v === 'accent')  return p.theme.colors.accent;
    return v || '#e2e8f0';
  }};
  border-radius: 16px;
  padding: 1rem 1.25rem;
  background: ${p => p.selected ? `${p.theme.colors.primary}14` : '#ffffff'};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06), 0 8px 24px rgba(0, 0, 0, 0.06);
  transition: transform 200ms cubic-bezier(0.23, 1, 0.32, 1), box-shadow 200ms ease;
  animation: ${fadeEntrance} 320ms cubic-bezier(0.23, 1, 0.32, 1) both;
  animation-delay: ${p => Math.min((p.$index || 0) * 60, 300)}ms;

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      transform: translateY(-3px);
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.07), 0 16px 32px rgba(0, 0, 0, 0.1);
    }
  }

  @media (prefers-reduced-motion: reduce) { animation: none; transition: none; }
`;

const OrderHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
`;

const OrderNumber = styled.h3`
  font-size: 0.9375rem;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.01em;
`;

const OrderDetails = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 0.5rem;
  margin-bottom: 0.625rem;
`;

const DetailItem = styled.div`
  .label {
    font-size: 0.68rem;
    color: #94a3b8;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    margin-bottom: 0.1875rem;
  }
  .value { font-size: 0.875rem; font-weight: 500; color: #0f172a; }
`;

const OrderPrintBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
  padding-top: 0.625rem;
  border-top: 1px solid ${p => p.theme.colors.primary}14;
`;

const PrintDocButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 5px 10px;
  border-radius: 8px;
  border: 1.5px solid #e2e8f0;
  background: white;
  color: #64748b;
  font-size: 0.75rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: border-color 150ms ease, color 150ms ease, transform 160ms ease-out;

  @media (hover: hover) and (pointer: fine) {
    &:hover { border-color: ${p => p.theme.colors.primary}; color: ${p => p.theme.colors.primary}; }
  }
  &:active { transform: scale(0.97); }

  @media (prefers-reduced-motion: reduce) { transition: none; &:active { transform: none; } }
`;

// ─── Inventory sub-tabs ────────────────────────────────────────────
const InventorySubTabs = styled.div`
  display: inline-flex;
  border-bottom: 2px solid #f1f5f9;
  margin-bottom: 1.25rem;
  gap: 0;
`;

const InventorySubTab = styled.button`
  padding: 0.5rem 1.25rem;
  border: none;
  border-bottom: 2px solid ${p => p.active ? p.theme.colors.primary : 'transparent'};
  margin-bottom: -2px;
  background: transparent;
  color: ${p => p.active ? p.theme.colors.primary : '#64748b'};
  font-weight: ${p => p.active ? '600' : '500'};
  font-size: 0.875rem;
  font-family: inherit;
  cursor: pointer;
  transition: color 150ms ease, border-color 150ms ease;
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;

  @media (hover: hover) and (pointer: fine) {
    &:hover { color: ${p => p.theme.colors.primary}; }
  }
  @media (prefers-reduced-motion: reduce) { transition: none; }
`;

const InventoryCard = styled.div`
  background: ${p => p.theme.colors.primary}14;
  border: 1px solid ${p => p.theme.colors.primary}66;
  border-radius: 10px;
  padding: 12px 16px;
  margin-bottom: 1rem;
  font-size: 0.8rem;
  color: #065f46;
  line-height: 1.5;
`;

// ─── Animated Donut Chart ──────────────────────────────────────────
const AnimatedDonut = ({ slices, total, caption, size = 144, stroke = 22 }) => {
  const radius      = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const [progress, setProgress] = useState(0);
  const rafRef = useRef(null);
  const slicesKey = (slices || []).map(s => s.value).join(',');

  useEffect(() => {
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduced) { setProgress(1); return; }

    setProgress(0);
    cancelAnimationFrame(rafRef.current);
    const t0 = performance.now();
    const dur = 1300;

    const tick = (now) => {
      const p = Math.min((now - t0) / dur, 1);
      setProgress(1 - Math.pow(1 - p, 3));   // ease-out cubic
      if (p < 1) rafRef.current = requestAnimationFrame(tick);
      else setProgress(1);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [slicesKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const valid = (slices || []).filter(s => Number(s.value) > 0);
  const sum   = valid.reduce((a, s) => a + Number(s.value), 0) || 1;

  // Pre-compute fixed angular offsets (based on final lengths).
  let cumFinal = 0;
  const arcs = valid.map(s => {
    const finalLen = (Number(s.value) / sum) * circumference;
    const arc = { ...s, finalLen, dashOffset: -cumFinal };
    cumFinal += finalLen;
    return arc;
  });

  // Animated centre label
  const isPercent    = typeof total === 'string' && String(total).endsWith('%');
  const numericTotal = isPercent
    ? parseFloat(total)
    : (typeof total === 'number' ? total : null);
  const displayTotal = numericTotal !== null
    ? (isPercent
        ? `${Math.round(numericTotal * progress)}%`
        : String(Math.round(numericTotal * progress)))
    : total;

  const cx = size / 2, cy = size / 2;

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      style={{ flexShrink: 0, overflow: 'visible' }}
      aria-hidden="true"
    >
      {/* Track */}
      <circle cx={cx} cy={cy} r={radius} fill="none" stroke="#d1fae5" strokeWidth={stroke} />

      {/* Animated arcs */}
      {arcs.map((arc, i) => {
        const len = arc.finalLen * progress;
        return (
          <circle
            key={i}
            cx={cx} cy={cy} r={radius}
            fill="none"
            stroke={arc.color}
            strokeWidth={stroke - 2}
            strokeDasharray={`${len} ${circumference - len}`}
            strokeDashoffset={arc.dashOffset}
            strokeLinecap="butt"
            transform={`rotate(-90 ${cx} ${cy})`}
            style={{ filter: `drop-shadow(0 0 4px ${arc.color}99)` }}
          />
        );
      })}

      {/* Centre value */}
      <text
        x={cx} y={cy - 5}
        textAnchor="middle"
        fontSize="18"
        fontWeight="800"
        fill="#0f172a"
        style={{ fontFamily: 'Inter, sans-serif', fontVariantNumeric: 'tabular-nums' }}
      >
        {displayTotal}
      </text>
      {caption && (
        <text
          x={cx} y={cy + 14}
          textAnchor="middle"
          fontSize="9"
          fontWeight="600"
          fill="#94a3b8"
          letterSpacing="0.06em"
          style={{ fontFamily: 'Inter, sans-serif' }}
        >
          {caption.toUpperCase()}
        </text>
      )}
    </svg>
  );
};

// ─── Main Component ────────────────────────────────────────────────
const ManagerDashboard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const { branding } = useBranding();
  const location = useLocation();
  const activeTab = (() => {
    const sub = location.pathname.replace(/^\/manager\/dashboard\/?/, '');
    return sub || 'overview';
  })();
  const [inventorySubTab, setInventorySubTab] = useState('lenses');
  const [orders, setOrders]                 = useState([]);
  const [ordersLoading, setOrdersLoading]   = useState(false);
  const [error, setError]                   = useState('');
  const [searchTerm, setSearchTerm]         = useState('');
  const [statusFilter, setStatusFilter]     = useState('all');
  const [highlightedOrderId, setHighlightedOrderId] = useState(null);

  const [confirmedOrders, setConfirmedOrders]   = useState([]);
  const [packagesLoading, setPackagesLoading]   = useState(false);
  const [selectedOrderIds, setSelectedOrderIds] = useState([]);
  const [storePackages, setStorePackages]       = useState([]);
  const [packageNotice, setPackageNotice]       = useState(null);
  const [creatingPackage, setCreatingPackage]   = useState(false);
  const [thirdPartyBusyId, setThirdPartyBusyId] = useState(null);
  const [handoffTarget, setHandoffTarget] = useState(null);
  const [stockTransferRequests, setStockTransferRequests] = useState([]);
  const [selectedPackageOrder, setSelectedPackageOrder] = useState(null);

  const [showLensOrderModal, setShowLensOrderModal] = useState(false);
  const [lensCatalog] = useState([]);
  const [lensOrderForm, setLensOrderForm] = useState({ lensProductId: '', sph: '', cyl: '', qty: 1, notes: '' });
  const [submittingLensOrder, setSubmittingLensOrder] = useState(false);
  const [peerRating, setPeerRating] = useState(null);
  const [peerRatingActive, setPeerRatingActive] = useState(false);
  // A branding logo can point at a file that no longer exists (store deleted
  // its upload, Storage migration). Fall back to the glasses mark instead of
  // leaving a broken image in the sidebar.
  const [logoBroken, setLogoBroken] = useState(false);

  // ── Admin "act as store" ─────────────────────────────────────────────
  // Regular managers use their own store. Admins have no store, so they pick
  // one and act as it; every server action still runs under the admin's token,
  // so the workflow log records the admin as the actor (built-in audit trail).
  const dashUser = useMemo(() => {
    try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch (_) { return {}; }
  }, []);
  const isAdminUser = ['admin', 'super_admin', 'advisor'].includes(dashUser.role);
  // Task switches decide which tabs and cards this person gets (admins get all).
  const can = (task) => hasTask(dashUser, task);
  const ownStoreId = dashUser?.store?.id || dashUser?.store?._id || dashUser?.storeId || null;
  const [actingStore, setActingStore] = useState(() => {
    try { return JSON.parse(localStorage.getItem('adminActingStore') || 'null'); } catch (_) { return null; }
  });
  const [storeList, setStoreList] = useState([]);
  const effectiveStoreId = ownStoreId || actingStore?.id || null;

  // Every role loads this now, not just admins: the admin store picker needs it,
  // and the package label needs the central warehouse's real name for its route
  // strip. GET /api/stores is tenant-scoped and open to any signed-in role.
  useEffect(() => {
    axios.get('/api/stores', { headers: { Authorization: `Bearer ${getToken()}` } })
      .then(({ data }) => setStoreList(data?.stores || (Array.isArray(data) ? data : [])))
      .catch(() => {});
  }, []);

  const centralStore = useMemo(
    () => storeList.find((s) => s.isCentralWarehouse) || null,
    [storeList]
  );

  // The operating model an admin set for this branch (own lens stock, own lab,
  // where work is sent). Until the store list loads we assume it keeps stock,
  // so the Lenses tab never flickers away from a store that does have it.
  const ownStore = useMemo(
    () => storeList.find((s) => String(s.id || s._id) === String(effectiveStoreId)) || null,
    [storeList, effectiveStoreId]
  );
  const capabilities = ownStore?.capabilities || null;
  const hasLensStock = capabilities ? capabilities.hasLensStock !== false : true;

  const handlePickStore = (store) => {
    setActingStore(store || null);
    if (store) localStorage.setItem('adminActingStore', JSON.stringify(store));
    else localStorage.removeItem('adminActingStore');
  };

  useEffect(() => {
    const loadPeerRating = async () => {
      try {
        const token = getToken();
        const storeId = effectiveStoreId;
        if (!token || !storeId) return;
        const { data } = await axios.get(`/api/stores/${storeId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setPeerRating(data?.store?.peerRating || null);
      } catch (_) {
        setPeerRating(null);
      }
    };
    loadPeerRating();
  }, [effectiveStoreId]);

  useEffect(() => {
    if (!peerRating) { setPeerRatingActive(false); return; }
    const check = () => {
      const now = new Date();
      const wd = typeof peerRating.visibleWeekday === 'number' ? peerRating.visibleWeekday : 1;
      const hStart = typeof peerRating.visibleHourStart === 'number' ? peerRating.visibleHourStart : 8;
      const hEnd = typeof peerRating.visibleHourEnd === 'number' ? peerRating.visibleHourEnd : 18;
      setPeerRatingActive(now.getDay() === wd && now.getHours() >= hStart && now.getHours() < hEnd);
    };
    check();
    const interval = setInterval(check, 60000);
    return () => clearInterval(interval);
  }, [peerRating]);

  useEffect(() => {
    if (highlightedOrderId && activeTab === 'orders') {
      setTimeout(() => {
        const el = document.querySelector(`[data-order-id="${highlightedOrderId}"]`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    }
  }, [highlightedOrderId, activeTab]);

  useEffect(() => {
    window.scrollTo(0, 0);
    setError('');
    if (activeTab === 'overview' || activeTab === 'orders') fetchOrders();
    if (activeTab === 'packages'  || activeTab === 'orders') fetchPackagesData();
  }, [activeTab, effectiveStoreId]); // eslint-disable-line react-hooks/exhaustive-deps

  const getToken = () => localStorage.getItem('token');

  // When an admin is acting as a store, scope orders to it; regular managers
  // keep the existing tenant-wide behaviour.
  const storeQuery = (isAdminUser && effectiveStoreId) ? `&storeId=${effectiveStoreId}` : '';

  const fetchOrders = async () => {
    setOrdersLoading(true);
    try {
      const { data } = await axios.get(`/api/orders?limit=500${storeQuery}`, { headers: { Authorization: `Bearer ${getToken()}` } });
      setOrders(data.orders || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load orders');
    } finally {
      setOrdersLoading(false);
    }
  };

  const fetchPackagesData = async () => {
    setPackagesLoading(true);
    setPackageNotice(null);
    try {
      const h = { Authorization: `Bearer ${getToken()}` };
      const [cr, pr, tr] = await Promise.allSettled([
        axios.get(`/api/orders?status=confirmed&limit=100${storeQuery}`, { headers: h }),
        axios.get('/api/packages?limit=50', { headers: h }),
        axios.get('/api/stock-transfer-requests?limit=100', { headers: h }),
      ]);
      setConfirmedOrders(cr.status === 'fulfilled' ? cr.value.data?.orders || [] : []);
      setStorePackages(pr.status === 'fulfilled' ? pr.value.data?.items  || [] : []);
      setStockTransferRequests(tr.status === 'fulfilled' ? tr.value.data?.requests || [] : []);
    } finally {
      setPackagesLoading(false);
    }
  };

  const toggleOrderSelected = id =>
    setSelectedOrderIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const handleCreatePackage = async () => {
    if (!selectedOrderIds.length) return;
    setCreatingPackage(true);
    setPackageNotice(null);
    try {
      const { data } = await axios.post('/api/packages', { orderIds: selectedOrderIds },
        { headers: { Authorization: `Bearer ${getToken()}` } });
      const pkg = data?.package;
      setPackageNotice({ kind: 'success', message: `Package ${pkg?.packageNumber || '(created)'} ready for courier pickup.`, pkg });
      setSelectedOrderIds([]);
      fetchPackagesData();
      fetchOrders(); // refresh the Orders tab so bundled orders drop out of the list
      if (pkg) handlePrintPackage(pkg);
    } catch (err) {
      setPackageNotice({ kind: 'error', message: err.response?.data?.message || err.message || 'Failed to create package' });
    } finally {
      setCreatingPackage(false);
    }
  };

  // The scan proves the box arrived. Anything transfer-shaped inside it still
  // has to be counted before it becomes stock, so hold onto the package and let
  // the checklist take over.
  const [receivingPackage, setReceivingPackage] = useState(null);
  const handlePackageScanSuccess = (payload) => {
    const pkg = payload?.package;
    const awaiting = payload?.transferReceipt?.awaitingCount || [];
    setPackageNotice({
      kind: 'success',
      message: awaiting.length
        ? `Package ${pkg?.packageNumber || ''} arrived — count the contents below to add them to stock.`
        : 'Package received. Updating list…',
    });
    setReceivingPackage(awaiting.length ? pkg : null);
    fetchPackagesData();
  };

  // Once it leaves with an outsider there is no scan and no account to trace it
  // by — the name is the only record of who took it, so the modal collects it
  // before anything is sent.
  const handleThirdPartyCourier = async (recipientName) => {
    const pkg = handoffTarget;
    if (!pkg?._id || !recipientName) return;
    setThirdPartyBusyId(pkg._id);
    setPackageNotice(null);
    try {
      const { data } = await axios.post(`/api/packages/${pkg._id}/third-party-courier`, { recipientName }, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      setPackageNotice({
        kind: 'success',
        message: `Package ${data.package?.packageNumber || pkg.packageNumber} handed to ${recipientName}.`,
      });
      setHandoffTarget(null);
      fetchPackagesData();
    } catch (err) {
      setPackageNotice({ kind: 'error', message: err.response?.data?.message || 'Failed to hand package to 3rd party courier.' });
    } finally {
      setThirdPartyBusyId(null);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/');
  };

  const submitLensOrder = async (e) => {
    e.preventDefault();
    if (!lensOrderForm.lensProductId) { setError('Pick a lens before sending the order.'); return; }
    setSubmittingLensOrder(true);
    setError('');
    try {
      const { data } = await axios.post('/api/stock-transfer-requests/lenses', {
        lensProductId: lensOrderForm.lensProductId,
        sph: lensOrderForm.sph === '' ? null : lensOrderForm.sph,
        cyl: lensOrderForm.cyl === '' ? null : lensOrderForm.cyl,
        qty: lensOrderForm.qty,
        notes: lensOrderForm.notes,
      }, { headers: { Authorization: `Bearer ${getToken()}` } });
      setPackageNotice({ kind: 'success', message: `Request sent — ${data.request?.qty || lensOrderForm.qty}× ${data.request?.productName || 'lens'} is ${String(data.request?.status || 'requested').replace(/_/g, ' ')}.` });
      setShowLensOrderModal(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send lens order.');
    } finally {
      setSubmittingLensOrder(false);
    }
  };

  const handlePrintPackage = async (pkg) => {
    if (!pkg) return;
    let full = pkg;
    if (!pkg.qrDataUrl || !Array.isArray(pkg.orders) || !pkg.orders[0]?.orderNumber) {
      try {
        const { data } = await axios.get(`/api/packages/${pkg._id}`, { headers: { Authorization: `Bearer ${getToken()}` } });
        full = data?.package || pkg;
      } catch (e) { console.warn('Failed to load full package for print:', e); }
    }
    openPrintIframe(buildPackageLabelHtml(full, {
      branding,
      defaultBranding: DEFAULT_BRANDING,
      centralStoreName: centralStore?.name || 'Central',
    }));
  };

  const filteredOrders = orders.filter(order => {
    const status = order.orderLifecycleState === 'CREATED_NOT_CONFIRMED' ? 'pending' :
      order.orderLifecycleState === 'CONFIRMED' ? 'confirmed' :
      order.orderLifecycleState === 'COMPLETED' ? 'completed' : (order.status || 'unknown');
    const matchSearch = order.orderNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.visitor?.gender?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchSearch && (statusFilter === 'all' || status === statusFilter);
  });

  const TAB_META = {
    overview:  { label: t('manager.tabOverview'),   icon: LayoutDashboard, live: true,  tasks: ['store.analytics', 'cashier.payments', 'store.team'] },
    orders:    { label: t('manager.tabOrders'),      icon: ShoppingCart,    live: true,  tasks: ['store.analytics'] },
    inventory: { label: t('manager.tabInventory'),   icon: Boxes,           live: false, tasks: ['store.stock'] },
    clients:   { label: t('manager.tabClients'),     icon: Users,           live: false },
    packages:  { label: t('manager.tabPackages'),    icon: PackageIcon,     live: false, tasks: ['store.packages'] },
    peerRating:{ label: t('manager.tabPeerRating'),  icon: Star,            live: false, hidden: true },
  };
  const tabAllowed = (key) => !TAB_META[key]?.tasks || hasAnyTask(dashUser, TAB_META[key].tasks);
  const firstAllowedTab = Object.keys(TAB_META).find((k) => !TAB_META[k].hidden && tabAllowed(k)) || 'clients';

  const cur = TAB_META[activeTab];

  // ── Sidebar identity ─────────────────────────────────────────────────
  // The store's own logo and name when branding has them, the Optiva
  // fallback otherwise. Same URL rule the receipts use: absolute stays,
  // a stored path gets a leading slash.
  const brandLogo = (!logoBroken && branding?.logo)
    ? (branding.logo.startsWith('http') ? branding.logo : `/${branding.logo}`)
    : null;
  const brandName = branding?.storeName || DEFAULT_BRANDING.storeName;

  const employeeName =
    dashUser?.fullName ||
    `${dashUser?.firstName || ''} ${dashUser?.lastName || ''}`.trim() ||
    dashUser?.name ||
    dashUser?.email ||
    '';
  const employeeInitials =
    `${dashUser?.firstName?.charAt(0) || ''}${dashUser?.lastName?.charAt(0) || ''}`.toUpperCase() ||
    (employeeName.charAt(0) || 'U').toUpperCase();
  const employeeRole = dashUser?.role
    ? t(`manager.roles.${dashUser.role}`, { defaultValue: String(dashUser.role).replace(/_/g, ' ') })
    : '';

  return (
    <Container>
      <Sidebar className="no-print">
        <SidebarHeader>
          <LogoRow>
            <LogoMark hasImage={!!brandLogo}>
              {brandLogo
                ? <img src={brandLogo} alt={brandName} onError={() => setLogoBroken(true)} />
                : <Glasses size={19} color="white" />}
            </LogoMark>
            <LogoText>
              <h2 title={brandName}>{brandName}</h2>
              <p>{t('manager.managerPanel')}</p>
            </LogoText>
          </LogoRow>
          <UserCard>
            <UserAvatar hasImage={!!dashUser?.profileImage}>
              {dashUser?.profileImage
                ? <img src={dashUser.profileImage} alt={employeeName} />
                : <div className="initials">{employeeInitials}</div>}
            </UserAvatar>
            <UserMeta>
              <div className="name" title={employeeName}>{employeeName || t('manager.managerPanel')}</div>
              <div className="role" title={[employeeRole, dashUser?.employeeId].filter(Boolean).join(' · ')}>
                {employeeRole}
                {dashUser?.employeeId && <> · <b>{dashUser.employeeId}</b></>}
              </div>
            </UserMeta>
          </UserCard>
        </SidebarHeader>

        <SidebarNav>
          <NavLabel>{t('manager.navMenu')}</NavLabel>
          {Object.entries(TAB_META).filter(([key, meta]) => !meta.hidden && tabAllowed(key)).map(([key, { label, icon: Icon }]) => (
            <NavItem key={key} active={activeTab === key} onClick={() => navigate('/manager/dashboard/' + key)}>
              <Icon size={17} />
              {label}
              {activeTab === key && <NavDot />}
            </NavItem>
          ))}
          <NavDivider />
          {/* Managers can also run the sales flow — start it clean */}
          {can('sales.create') && (
            <NavItem onClick={() => { clearSalesFlowSession(); navigate('/visitor-entry'); }}>
              <Glasses size={17} />
              {t('manager.newVisitSales')}
            </NavItem>
          )}
          {peerRatingActive && peerRating && (
            <ManagerPeerRatingBanner type="button" onClick={() => navigate('/manager/dashboard/peerRating')}>
              <div className="title"><ClipboardList size={16} /> {t('manager.peerRatingActive')}</div>
              <div className="note">
                {peerRating.setupNote || 'Tap here to rate your peers today.'}
              </div>
            </ManagerPeerRatingBanner>
          )}
        </SidebarNav>

        <SidebarFooter>
          <LogoutButton onClick={handleLogout}><LogOut size={16} />{t('manager.signOut')}</LogoutButton>
        </SidebarFooter>
      </Sidebar>

      <MainContent>
        <PageHeader className="no-print">
          <PageTitleGroup>
            <Title>{cur.label}</Title>
            {cur.live && (
              <LiveBadge><LiveDot />{t('manager.live')}</LiveBadge>
            )}
          </PageTitleGroup>
        </PageHeader>

        <ContentArea>
          {isAdminUser && (
            <div className="no-print" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap', margin: '0 0 1rem', padding: '0.7rem 1rem', background: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: '12px' }}>
              <span style={{ fontWeight: 600, color: '#3730a3', fontSize: '0.9rem' }}>
                {actingStore ? t('manager.managingStoreAsAdmin') : t('manager.selectAStoreToManage')}
              </span>
              <select
                value={actingStore?.id || ''}
                onChange={(e) => {
                  const s = storeList.find(st => String(st.id || st._id) === e.target.value);
                  handlePickStore(s ? { id: s.id || s._id, name: s.name } : null);
                }}
                style={{ padding: '0.4rem 0.6rem', borderRadius: '8px', border: '1px solid #c7d2fe', fontSize: '0.875rem', background: '#fff', color: '#1e293b' }}
              >
                <option value="">{t('manager.chooseAStore')}</option>
                {storeList.map(s => (
                  <option key={s.id || s._id} value={s.id || s._id}>{s.name}</option>
                ))}
              </select>
              {actingStore && (
                <span style={{ fontSize: '0.78rem', color: '#6366f1' }}>{t('manager.anyChangesAreLoggedUnder')}</span>
              )}
            </div>
          )}
          {error && <ErrorMessage>{error}</ErrorMessage>}

          {TAB_META[activeTab] && !tabAllowed(activeTab) && activeTab !== firstAllowedTab && (
            <Navigate to={'/manager/dashboard/' + firstAllowedTab} replace />
          )}

          {activeTab === 'overview' ? (
            <OverviewTab orders={orders} loading={ordersLoading}
              showAnalytics={can('store.analytics')} showPayments={can('cashier.payments')} showTeam={can('store.team')}
              onOrderClick={id => { setHighlightedOrderId(id); navigate('/manager/dashboard/orders'); setTimeout(() => setHighlightedOrderId(null), 3000); }} />

          ) : activeTab === 'orders' ? (
            <div>
              {packageNotice && (
                <PackageNotice kind={packageNotice.kind}>
                  <span>{packageNotice.message}</span>
                  {packageNotice.pkg && (
                    <button type="button" onClick={() => handlePrintPackage(packageNotice.pkg)}>
                      <Printer size={13} /> {t('manager.reprintLabel')}
                    </button>
                  )}
                </PackageNotice>
              )}

              <SearchBar>
                <SearchInputWrap>
                  <Search size={15} />
                  <SearchInput type="text" placeholder={t('manager.searchByOrderNumberOr')}
                    value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
                </SearchInputWrap>
                <FilterSelect value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                  <option value="all">{t('manager.allStatuses')}</option>
                  <option value="pending">{t('manager.pending')}</option>
                  <option value="confirmed">{t('manager.confirmed')}</option>
                  <option value="completed">{t('manager.completed')}</option>
                </FilterSelect>
              </SearchBar>

              <SelectionBar>
                <div>
                  <strong>{selectedOrderIds.length}</strong> {t('manager.orderSSelected')}
                  {selectedOrderIds.length > 0 && (
                    <SelectionClear type="button" onClick={() => setSelectedOrderIds([])}>{t('manager.clear')}</SelectionClear>
                  )}
                </div>
                <CombineButton type="button" disabled={!selectedOrderIds.length || creatingPackage} onClick={handleCreatePackage}>
                  <PackageIcon size={14} />
                  {creatingPackage ? t('manager.creating') : t('manager.bundleIntoPackage', { count: selectedOrderIds.length })}
                </CombineButton>
              </SelectionBar>

              <OrdersList>
                {ordersLoading ? (
                  <div style={{ textAlign: 'center', padding: '3rem', color: '#94a3b8' }}>
                    <RefreshCw size={28} style={{ animation: 'spin 1s linear infinite' }} />
                  </div>
                ) : filteredOrders.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '3rem', color: '#94a3b8', fontSize: '0.875rem' }}>{t('manager.noOrdersFound')}</div>
                ) : (
                  filteredOrders.map((order, idx) => {
                    // Real lifecycle stage from the workflow trail (e.g. received_at_store),
                    // falling back to the enum status. The enum column stays 'confirmed'
                    // through the whole journey, so it can't be trusted on its own.
                    const stage = order.currentStage || order.orderLifecycleState || order.status || '';
                    // Only a fresh, still-at-store confirmed order that hasn't progressed
                    // (or been bundled) can be sent to central.
                    const bundleable = (stage === 'confirmed' || stage === 'CONFIRMED') && !order.inPackage;
                    const selected = selectedOrderIds.includes(order._id);
                    return (
                      <OrderCard key={order._id} selected={selected} $index={idx} $lifecycle={stage}
                        data-order-id={order._id}
                        style={highlightedOrderId === order._id ? { boxShadow: `0 0 0 3px ${theme.colors.accent}`, transition: 'box-shadow 0.3s' } : {}}>
                        <OrderHeader>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem' }}>
                            {bundleable && (
                              <input type="checkbox" checked={selected} onChange={() => toggleOrderSelected(order._id)}
                                style={{ width: 16, height: 16, cursor: 'pointer', accentColor: theme.colors.primary }} />
                            )}
                            <OrderNumber>{order.orderNumber || order.orderCode || t('manager.draftOrder')}</OrderNumber>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                            {order.inPackage && (
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.7rem', fontWeight: 700, color: '#0369a1', background: '#e0f2fe', border: '1px solid #bae6fd', borderRadius: '999px', padding: '0.15rem 0.55rem' }}>
                                <PackageIcon size={11} /> {t('manager.inPackage')}
                              </span>
                            )}
                            <StatusBadge status={stage}>
                              {tOrderStatus(t, stage)}
                            </StatusBadge>
                          </div>
                        </OrderHeader>

                        <OrderDetails>
                          <DetailItem>
                            <div className="label">{t('manager.customer')}</div>
                            <div className="value">{tVisitorDemo(t, order.visitor?.gender, order.visitor?.ageGroup)}</div>
                          </DetailItem>
                          <DetailItem>
                            <div className="label">{t('manager.orderDate')}</div>
                            <div className="value">{new Date(order.createdAt || order.orderDate).toLocaleDateString()}</div>
                          </DetailItem>
                          <DetailItem>
                            <div className="label">{t('manager.total')}</div>
                            <div className="value" style={{ fontWeight: 700, color: '#0f172a', fontVariantNumeric: 'tabular-nums' }}>
                              {order.total?.toLocaleString()} {t('manager.dzd')}
                            </div>
                          </DetailItem>
                          <DetailItem>
                            <div className="label">{t('manager.payment')}</div>
                            <div className="value">{tPaymentStatus(t, order.payment?.status)}</div>
                          </DetailItem>
                          {order.salesPerson && (
                            <DetailItem>
                              <div className="label">{t('manager.salesPerson')}</div>
                              <div className="value">{order.salesPerson.firstName} {order.salesPerson.lastName}</div>
                            </DetailItem>
                          )}
                          {order.technician && (
                            <DetailItem>
                              <div className="label">{t('manager.technician')}</div>
                              <div className="value">{order.technician.firstName} {order.technician.lastName}</div>
                            </DetailItem>
                          )}
                        </OrderDetails>

                        <OrderPrintBar>
                          <PrintDocButton type="button" onClick={() => printReceiptHtml({ orderData: order, copyType: 'invoice', branding })}>
                            <FileText size={13} /> {t('manager.invoice')}
                          </PrintDocButton>
                          <PrintDocButton type="button" onClick={() => printReceiptHtml({ orderData: order, copyType: 'customer', branding })}>
                            <Printer size={13} /> {t('manager.receipt')}
                          </PrintDocButton>
                          <PrintDocButton type="button" onClick={() => printEquipmentPassport({ orderData: order, branding })}>
                            <Truck size={13} /> {t('manager.equipmentPassport')}
                          </PrintDocButton>
                        </OrderPrintBar>
                      </OrderCard>
                    );
                  })
                )}
              </OrdersList>
            </div>

          ) : activeTab === 'inventory' ? (
            <InventoryTab inventorySubTab={inventorySubTab} setInventorySubTab={setInventorySubTab}
              hasLensStock={hasLensStock}
              handleFrameScanSuccess={payload => setPackageNotice({
                kind: 'success',
                message: `Pulled ${payload?.qty ?? 1}× ${payload?.product?.name || 'frame'} from central. Store stock: ${payload?.storeRemaining ?? '?'}.`,
              })} />

          ) : activeTab === 'clients' ? (
            <ClientList />

          ) : activeTab === 'packages' ? (
            <PackagesTab
              confirmedOrders={confirmedOrders} storePackages={storePackages}
              packagesLoading={packagesLoading} selectedOrderIds={selectedOrderIds}
              toggleOrderSelected={toggleOrderSelected} handleCreatePackage={handleCreatePackage}
              handlePackageScanSuccess={handlePackageScanSuccess} packageNotice={packageNotice}
              receivingPackage={receivingPackage} onReceiveDone={() => { setReceivingPackage(null); fetchPackagesData(); }}
              onCountIn={setReceivingPackage}
              creatingPackage={creatingPackage} onRefresh={fetchPackagesData}
              handlePrintPackage={handlePrintPackage}
              handleThirdPartyCourier={setHandoffTarget}
              thirdPartyBusyId={thirdPartyBusyId}
              stockTransferRequests={stockTransferRequests}
              onOrderDetails={setSelectedPackageOrder} />
          ) : activeTab === 'peerRating' ? (
            <PeerRatingPage />
          ) : null}

          {selectedPackageOrder && (
            <OrderDetailsModal order={selectedPackageOrder} onClose={() => setSelectedPackageOrder(null)} />
          )}

          {/* Lens order modal */}
          {showLensOrderModal && (
            <ModalOverlay onClick={() => setShowLensOrderModal(false)}>
              <ModalContent onClick={e => e.stopPropagation()}>
                <ModalHeader>
                  <ModalTitle>{t('manager.orderLensesFromCentral')}</ModalTitle>
                  <CloseButton onClick={() => setShowLensOrderModal(false)} aria-label={t('manager.close')}><X size={18} /></CloseButton>
                </ModalHeader>
                <ModalBody>
                  <form onSubmit={submitLensOrder}>
                    <FormGroup>
                      <Label htmlFor="lens-sel">{t('manager.lens')}</Label>
                      <Select id="lens-sel" value={lensOrderForm.lensProductId}
                        onChange={e => setLensOrderForm({ ...lensOrderForm, lensProductId: e.target.value })} required>
                        <option value="">{t('manager.pickALens')}</option>
                        {lensCatalog.map(lp => <option key={lp._id} value={lp._id}>{lp.name}</option>)}
                      </Select>
                    </FormGroup>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem' }}>
                      <FormGroup>
                        <Label>{t('manager.sph')}</Label>
                        <Input type="number" step="0.25" value={lensOrderForm.sph} placeholder="-1.50"
                          onChange={e => setLensOrderForm({ ...lensOrderForm, sph: e.target.value })} />
                      </FormGroup>
                      <FormGroup>
                        <Label>{t('manager.cyl')}</Label>
                        <Input type="number" step="0.25" value={lensOrderForm.cyl} placeholder="-0.75"
                          onChange={e => setLensOrderForm({ ...lensOrderForm, cyl: e.target.value })} />
                      </FormGroup>
                      <FormGroup>
                        <Label>{t('manager.qty')}</Label>
                        <Input type="number" min="1" value={lensOrderForm.qty} required
                          onChange={e => setLensOrderForm({ ...lensOrderForm, qty: parseInt(e.target.value) || 1 })} />
                      </FormGroup>
                    </div>
                    <FormGroup>
                      <Label>{t('manager.notes')}</Label>
                      <Textarea value={lensOrderForm.notes} placeholder={t('manager.urgencyCustomerNameOrAnything')}
                        onChange={e => setLensOrderForm({ ...lensOrderForm, notes: e.target.value })} />
                    </FormGroup>
                    <ButtonGroup>
                      <Button type="button" onClick={() => setShowLensOrderModal(false)}>{t('manager.cancel')}</Button>
                      <Button type="submit" primary disabled={submittingLensOrder}>
                        {submittingLensOrder ? t('manager.sending') : t('manager.sendOrder')}
                      </Button>
                    </ButtonGroup>
                  </form>
                </ModalBody>
              </ModalContent>
            </ModalOverlay>
          )}
        </ContentArea>
      </MainContent>

      <ThirdPartyHandoffModal
        open={Boolean(handoffTarget)}
        packageNumber={handoffTarget?.packageNumber}
        busy={thirdPartyBusyId === handoffTarget?._id}
        onCancel={() => setHandoffTarget(null)}
        onConfirm={handleThirdPartyCourier}
      />
    </Container>
  );
};

export default ManagerDashboard;

// ─── Helpers ───────────────────────────────────────────────────────
const fmtMoney = n => `${(Number(n) || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })} DZD`;
const fmtDate = value => {
  if (!value) return 'N/A';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? 'N/A' : d.toLocaleDateString();
};
const personName = person => {
  if (!person) return 'N/A';
  return [person.firstName, person.lastName].filter(Boolean).join(' ') || person.name || person.email || 'N/A';
};
const customerName = visitor => {
  if (!visitor) return 'Visitor';
  return [visitor.firstName, visitor.lastName].filter(Boolean).join(' ') || visitor.name || visitor.gender || 'Visitor';
};
const estimatedDaysLabel = (item, order) => {
  const days = item?.estimatedDays ?? item?.specifications?.estimatedDays ?? item?.specifications?.defaultDeliveryDaysMax;
  // Trial lenses (progressive) add 10 days to the lens line, as printed on
  // the passport and receipt.
  const isLens = (item?.type || item?.item_type) === 'lens';
  const extra = isLens && order && hasTrialLens(order) ? TRIAL_LENS_EXTRA_DAYS : 0;
  if ((days === null || days === undefined || days === '' || Number(days) <= 0) && !extra) return '';
  const base = Number(days) > 0 ? Number(days) : 0;
  return extra
    ? `Estimated days: ${base + extra} (${base} + ${extra} trial lenses)`
    : `Estimated days: ${base}`;
};
const hasCorrectionValues = eye => {
  if (!eye || typeof eye !== 'object') return false;
  return ['sphere', 'sph', 'cylinder', 'cyl', 'axis', 'add', 'addition'].some(key => eye[key] !== null && eye[key] !== undefined && eye[key] !== '');
};
const describeSpecs = specs => {
  if (!specs || typeof specs !== 'object') return '';
  return Object.entries(specs)
    .filter(([key, value]) => key === 'estimatedDays' && value !== null && value !== undefined && value !== '' && Number(value) > 0)
    .map(([key, value]) => `${key}: ${typeof value === 'object' ? JSON.stringify(value) : value}`)
    .join(' · ');
};
const orderItemsForDisplay = order => {
  const directItems = order.items || order.orderItems || order.order_items || [];
  if (directItems.length) return directItems;
  const rawItems = order.raw_data?.items || order.rawData?.items || [];
  if (Array.isArray(rawItems) && rawItems.length) return rawItems;
  const recommendation = order.prescription?.selectedRecommendation;
  return recommendation ? [{
    type: 'lens',
    name: recommendation.name || 'Prescription lens',
    brand: recommendation.brand || '',
    model: recommendation.indexValue ? `Index ${recommendation.indexValue}` : '',
    quantity: 1,
    unitPrice: recommendation.price,
    totalPrice: recommendation.price,
    specifications: recommendation
  }] : [];
};
const formatCorrectionValue = value => {
  if (value === null || value === undefined || value === '') return '—';
  return value;
};
const correctionRowsForDisplay = prescription => {
  if (!prescription) return [];
  const rows = [];
  const addPair = (label, right, left) => {
    if (!hasCorrectionValues(right) && !hasCorrectionValues(left)) return;
    rows.push({
      label,
      od: right || {},
      og: left || {}
    });
  };
  if (prescription.prescriptionType === 'progressive') {
    addPair('Distance', prescription.progressiveRightEyeFar || prescription.rightEyeFar || prescription.rightEye, prescription.progressiveLeftEyeFar || prescription.leftEyeFar || prescription.leftEye);
    addPair('Near', prescription.progressiveRightEyeCloseUp || prescription.rightEyeCloseUp, prescription.progressiveLeftEyeCloseUp || prescription.leftEyeCloseUp);
  } else if (prescription.singleVisionCorrectionType === 'closeup') {
    addPair('Near', prescription.rightEyeCloseUp || prescription.rightEye, prescription.leftEyeCloseUp || prescription.leftEye);
  } else if (prescription.singleVisionCorrectionType === 'far') {
    addPair('Distance', prescription.rightEyeFar || prescription.rightEye, prescription.leftEyeFar || prescription.leftEye);
  } else {
    addPair('Distance', prescription.rightEyeFar || prescription.rightEye, prescription.leftEyeFar || prescription.leftEye);
    addPair('Near', prescription.rightEyeCloseUp, prescription.leftEyeCloseUp);
    addPair('Progressive distance', prescription.progressiveRightEyeFar, prescription.progressiveLeftEyeFar);
    addPair('Progressive near', prescription.progressiveRightEyeCloseUp, prescription.progressiveLeftEyeCloseUp);
  }
  return rows;
};

const OrderDetailsModal = ({ order, onClose }) => {
  const { t } = useTranslation();
  const items = orderItemsForDisplay(order);
  const birthday = order.prescription?.patientBirthday || order.visitor?.patientBirthday || order.visitor?.birthday || order.raw_data?.visitor?.patientBirthday || order.raw_data?.patientBirthday;
  const m = order.measurements || {};
  const correctionRows = correctionRowsForDisplay(order.prescription);
  const measurementRows = [
    ['PD OD', m.pdOD], ['PD OG', m.pdOG], ['PD', m.pd],
    ['Mounting height OD', m.mountingHeightOD || m.mountingHeight],
    ['Mounting height OG', m.mountingHeightOG],
    // Two-pair sale: pair 2 (close up) measures.
    ['Close up · mounting OD', m.near?.mountingHeightOD],
    ['Close up · mounting OG', m.near?.mountingHeightOG],
    ['Close up · PD OD', m.near?.pdOD],
    ['Close up · PD OG', m.near?.pdOG],
    ['Segment height', m.segFittingHeight || m.segmentHeight],
    ['Vertex distance', m.vertexDistance],
    ['Pantoscopic tilt', m.pantoscopicTilt],
    ['Wrap angle', m.wrapAngle],
  ].filter(([, value]) => value !== null && value !== undefined && value !== '');

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={e => e.stopPropagation()} style={{ maxWidth: 820 }}>
        <ModalHeader>
          <ModalTitle>{t('manager.order')} {order.orderNumber || order.order_number || 'Details'}</ModalTitle>
          <CloseButton onClick={onClose} aria-label={t('manager.close')}><X size={18} /></CloseButton>
        </ModalHeader>
        <ModalBody>
          <OrderDetails style={{ marginBottom: '1rem' }}>
            <DetailItem><div className="label">{t('manager.customer')}</div><div className="value">{customerName(order.visitor)}</div></DetailItem>
            <DetailItem><div className="label">{t('manager.dateOfBirth')}</div><div className="value">{fmtDate(birthday)}</div></DetailItem>
            <DetailItem><div className="label">{t('manager.salesPerson')}</div><div className="value">{personName(order.salesPerson)}</div></DetailItem>
            <DetailItem><div className="label">{t('manager.store')}</div><div className="value">{order.store?.name || t('manager.na')}</div></DetailItem>
            <DetailItem><div className="label">{t('manager.orderDate')}</div><div className="value">{fmtDate(order.orderDate || order.order_date || order.createdAt)}</div></DetailItem>
            <DetailItem><div className="label">{t('manager.status')}</div><div className="value">{tOrderStatus(t, order.currentStage || order.orderLifecycleState || order.status)}</div></DetailItem>
            <DetailItem><div className="label">{t('manager.payment')}</div><div className="value">{tPaymentStatus(t, order.payment?.status)}</div></DetailItem>
            <DetailItem><div className="label">{t('manager.total')}</div><div className="value" style={{ fontWeight: 700 }}>{fmtMoney(order.total)}</div></DetailItem>
          </OrderDetails>

          {items.length === 0 ? (
            <div style={{ color:'#94a3b8', fontStyle:'italic', fontSize:'0.875rem', marginBottom:'1rem' }}>{t('manager.noItemsRecorded')}</div>
          ) : (
            <div style={{ display:'flex', flexDirection:'column', gap:'0.5rem', marginBottom:'1rem' }}>
              {items.map((item, idx) => (
                <div key={item._id || item.id || idx} style={{ border:'1px solid #e2e8f0', borderRadius:12, padding:'0.75rem', background:'#f8fafc' }}>
                  <div style={{ display:'flex', justifyContent:'space-between', gap:'0.75rem', alignItems:'flex-start' }}>
                    <div>
                      <strong style={{ color:'#0f172a' }}>{item.name || item.model || item.type || 'Item'}</strong>
                      <div style={{ color:'#64748b', fontSize:'0.8125rem', marginTop:2 }}>
                        {[item.type || item.itemType, item.brand, item.model, item.material, item.color, item.size].filter(Boolean).join(' · ') || 'No item details'}
                      </div>
                    </div>
                    <div style={{ color:'#0f172a', fontWeight:700, whiteSpace:'nowrap' }}>{fmtMoney(item.totalPrice || item.total_price || item.unitPrice || item.unit_price)}</div>
                  </div>
                  {estimatedDaysLabel(item, order) && (
                    <div style={{ color:'#475569', fontSize:'0.78125rem', marginTop:'0.5rem', lineHeight:1.45 }}>{estimatedDaysLabel(item, order)}</div>
                  )}
                </div>
              ))}
            </div>
          )}

          <h3 style={{ margin:'0 0 0.625rem', fontSize:'0.9375rem', color:'#0f172a' }}>{t('manager.lensCorrection')}</h3>
          {correctionRows.length === 0 ? (
            <div style={{ color:'#94a3b8', fontStyle:'italic', fontSize:'0.875rem', marginBottom:'1rem' }}>{t('manager.noCorrectionRecorded')}</div>
          ) : (
            <div style={{ display:'flex', flexDirection:'column', gap:'0.5rem', marginBottom:'1rem' }}>
              {correctionRows.map(row => (
                <div key={row.label} style={{ border:'1px solid #e2e8f0', borderRadius:12, overflow:'hidden', background:'#fff' }}>
                  <div style={{ padding:'0.5rem 0.75rem', background:'#f8fafc', fontWeight:700, color:'#0f172a', fontSize:'0.8125rem' }}>{row.label}</div>
                  <div style={{ display:'grid', gridTemplateColumns:'80px repeat(4, 1fr)', gap:0, fontSize:'0.8125rem' }}>
                    {['Eye','SPH','CYL','AXIS','ADD'].map(h => <div key={h} style={{ padding:'0.45rem 0.6rem', color:'#94a3b8', fontWeight:700, borderTop:'1px solid #e2e8f0' }}>{h}</div>)}
                    {[
                      ['OD', row.od],
                      ['OG', row.og],
                    ].map(([eye, values]) => (
                      <React.Fragment key={`${row.label}-${eye}`}>
                        <div style={{ padding:'0.45rem 0.6rem', fontWeight:700, color:'#0f172a', borderTop:'1px solid #e2e8f0' }}>{eye}</div>
                        <div style={{ padding:'0.45rem 0.6rem', borderTop:'1px solid #e2e8f0' }}>{formatCorrectionValue(values.sphere ?? values.sph)}</div>
                        <div style={{ padding:'0.45rem 0.6rem', borderTop:'1px solid #e2e8f0' }}>{formatCorrectionValue(values.cylinder ?? values.cyl)}</div>
                        <div style={{ padding:'0.45rem 0.6rem', borderTop:'1px solid #e2e8f0' }}>{formatCorrectionValue(values.axis)}</div>
                        <div style={{ padding:'0.45rem 0.6rem', borderTop:'1px solid #e2e8f0' }}>{formatCorrectionValue(values.addition ?? values.add)}</div>
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          <h3 style={{ margin:'0 0 0.625rem', fontSize:'0.9375rem', color:'#0f172a' }}>{t('manager.measurements')}</h3>
          {measurementRows.length === 0 ? (
            <div style={{ color:'#94a3b8', fontStyle:'italic', fontSize:'0.875rem' }}>{t('manager.noMeasurementsRecorded')}</div>
          ) : (
            <OrderDetails>{measurementRows.map(([label, value]) => (
              <DetailItem key={label}><div className="label">{label}</div><div className="value">{value}</div></DetailItem>
            ))}</OrderDetails>
          )}

          {(order.notes || order.specialInstructions || order.special_instructions) && (
            <div style={{ marginTop:'1rem', padding:'0.75rem', borderRadius:12, background:'#fffbeb', color:'#92400e', fontSize:'0.875rem' }}>
              <strong>{t('manager.notes2')}</strong> {order.notes || order.specialInstructions || order.special_instructions}
            </div>
          )}
        </ModalBody>
      </ModalContent>
    </ModalOverlay>
  );
};

// ─── OverviewTab ───────────────────────────────────────────────────
const OverviewTab = ({ orders, loading, onOrderClick, showAnalytics = true, showPayments = true, showTeam = true }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const today = new Date();
  const isoDay = d => {
    const y = d.getFullYear(), m = String(d.getMonth()+1).padStart(2,'0'), dy = String(d.getDate()).padStart(2,'0');
    return `${y}-${m}-${dy}`;
  };
  const todayStr = isoDay(today);

  const [from, setFrom]     = useState(todayStr);
  const [to,   setTo]       = useState(todayStr);
  const [preset, setPreset] = useState('today');
  const [qty, setQty]       = useState(null);
  const [qtyLoading, setQtyLoading] = useState(false);
  const [qtyDrill,     setQtyDrill]     = useState([]);
  const [paymentDrill, setPaymentDrill] = useState(null);
  const [ordersDrill,  setOrdersDrill]  = useState(null);
  const [expandedRow,  setExpandedRow]  = useState({});

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setQtyLoading(true);
        const p = new URLSearchParams();
        if (from) p.set('startDate', from);
        if (to)   p.set('endDate',   to);
        const { data } = await axios.get(`/api/analytics/manager-quantity?${p}`,
          { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
        if (!cancelled) setQty(data || null);
      } catch { if (!cancelled) setQty(null); }
      finally  { if (!cancelled) setQtyLoading(false); }
    };
    load();
    return () => { cancelled = true; };
  }, [from, to]);

  const applyPreset = p => {
    setPreset(p);
    const now = new Date();
    if (p === 'today') { const t = isoDay(now); setFrom(t); setTo(t); }
    else if (p === 'week')  { const s = new Date(now); s.setDate(now.getDate()-6);  setFrom(isoDay(s)); setTo(isoDay(now)); }
    else if (p === 'month') { const s = new Date(now); s.setDate(now.getDate()-29); setFrom(isoDay(s)); setTo(isoDay(now)); }
    else if (p === 'all')   { setFrom(''); setTo(''); }
  };

  const inRange = o => {
    if (!from && !to) return true;
    const raw = o.orderDate || o.createdAt;
    if (!raw) return false;
    const d = new Date(raw);
    if (isNaN(d.getTime())) return false;
    if (from && d < new Date(from+'T00:00:00')) return false;
    if (to   && d > new Date(to  +'T23:59:59.999')) return false;
    return true;
  };
  const lifecycleStatus = o => {
    const workflow = Array.isArray(o.workflow) ? o.workflow : [];
    if (workflow.some(item => item?.status === 'completed')) return 'completed';
    const latest = workflow.length ? workflow[workflow.length - 1]?.status : null;
    if (latest === 'delivered') return 'delivered';
    return o.status;
  };
  // Sales widgets must count SALES, not every row in the date window. Mirrors
  // the server's `soldStatuses` in routes/supabaseOps.js exactly, so the
  // client-side fallback totals agree with the API's instead of drifting.
  //
  // This became load-bearing when the Rx step began creating `pending` draft
  // orders: before that a draft could not exist, so an unfiltered window was
  // harmless. Now every abandoned prescription — and every order cancelled with
  // an "unsuccessful order" reason — would otherwise be reported as sold, and
  // the quantity donut falls back to counting `inWindow` whenever the API
  // reports zero sold items (see effectiveQty below).
  const SOLD_ORDER_STATUSES = new Set([
    'confirmed', 'paid', 'packaged', 'packaged_at_store',
    'in_transit_to_central', 'at_central_inbound', 'in_transit_to_lab', 'at_lab',
    'in_production', 'manufacturing', 'quality_check', 'returning_to_central',
    'received_back_at_central', 'at_central_outbound', 'in_transit_to_store',
    'ready_for_pickup', 'out_for_delivery', 'delivered', 'completed',
  ]);
  const inWindow = (orders || []).filter(o => inRange(o) && SOLD_ORDER_STATUSES.has(lifecycleStatus(o)));

  const normalizeDashboardItem = (item = {}) => {
    const specs = item.specifications || item.specs || {};
    const haystack = [
      item.type,
      item.itemType,
      item.item_type,
      item.productType,
      item.product_type,
      item.category,
      item.productModel,
      item.product_model,
      item.model,
      item.name,
      item.lensType,
      specs.type,
      specs.itemType,
      specs.productType,
      specs.category,
      specs.lensType,
      specs.prescriptionType,
    ].filter(Boolean).join(' ').toLowerCase();
    const type = /contact[\s_-]*lens|contactlens|\bcontact\b/.test(haystack)
      ? 'contact_lens'
      : /\bframe\b|frames|monture/.test(haystack)
        ? 'frame'
        : /\blens\b|lenses|verre|single vision|simple vision|progressive|\brx\b/.test(haystack)
          ? 'lens'
          : String(item.type || item.itemType || item.item_type || '').toLowerCase();
    return {
      type,
      name: item.name || item.productName || item.product_name || item.label || item.model || 'Unnamed item',
      model: item.model || '',
      quantity: item.quantity ?? item.qty ?? item.count ?? 1,
      specifications: specs
    };
  };

  const buildFallbackQuantity = () => {
    const out = {
      lensQuantities: { intern: 0, extern: 0, rx: 0, progressive: 0, progressivePremium: 0, progressiveFair: 0, progressiveModest: 0 },
      frameQuantities: { total: 0 },
      contactLensQuantities: { total: 0, hard: 0, soft: 0, therapeutic: 0 },
      lensDetails: { intern: [], extern: [], rx: [], progressivePremium: [], progressiveFair: [], progressiveModest: [] },
      frameDetails: [],
      contactLensDetails: { hard: [], soft: [], therapeutic: [] }
    };
    const maps = {
      intern: new Map(), extern: new Map(), rx: new Map(),
      progressivePremium: new Map(), progressiveFair: new Map(), progressiveModest: new Map(),
      frames: new Map(), hard: new Map(), soft: new Map(), therapeutic: new Map()
    };
    const add = (map, name, count) => {
      const key = name || 'Unnamed item';
      const row = map.get(key) || { name: key, count: 0, sales: [] };
      row.count += count;
      map.set(key, row);
    };
    const rows = (map) => Array.from(map.values()).sort((a, b) => b.count - a.count);
    // Mirrors dispoBucket in server/routes/supabaseOps.js. The stored value can
    // be 'Stock Int'/'Stock Ext'/'Rx' (catalog), 'stock_int'/'stock_ext'/'rx'
    // (client dispo), 'intern'/'extern'/'rx' (server), or a bare 'stock' that
    // says nothing — matching only 'extern' sent every one of those to intern.
    // Unlike the server this fallback has no catalog to consult, so a bare
    // 'stock' still lands in intern.
    const source = (specs = {}) => {
      const raw = String(specs.sourceType || specs.source || specs.stockType || specs.availability || specs.dispo || '').toLowerCase();
      if (/\brx\b/.test(raw)) return 'rx';
      if (raw.includes('ext')) return 'extern';
      return 'intern';
    };
    const isProg = (item, order) => {
      const specs = item.specifications || {};
      return [item.name, item.model, specs.prescriptionType, specs.lensType, specs.type, order.prescription?.prescriptionType, order.prescriptionType]
        .filter(Boolean).join(' ').toLowerCase().includes('progressive');
    };
    // Progressive tier = which slot the salesperson picked (recommendation →
    // fair, Other-Up → premium, Other-Down → modest). Mirrors progressiveTierKey
    // on the server; Payment.js freezes it onto the line as specifications.tier.
    const progTier = (item, order) => {
      const specs = item.specifications || {};
      const explicit = String(specs.tier || '').toLowerCase();
      if (explicit.includes('premium')) return 'progressivePremium';
      if (explicit.includes('modest') || explicit.includes('basic')) return 'progressiveModest';
      if (explicit.includes('fair')) return 'progressiveFair';
      const rec = order?.prescription?.selectedRecommendation || null;
      const direction = String(specs.lensDirection || specs.direction || rec?.direction || '').toLowerCase();
      if (direction === 'up') return 'progressivePremium';
      if (direction === 'down') return 'progressiveModest';
      return 'progressiveFair';
    };
    const contactBucket = (item) => {
      const specs = item.specifications || {};
      const raw = [specs.subtype, specs.type, specs.lensType, specs.category, item.name, item.model].filter(Boolean).join(' ').toLowerCase();
      if (raw.includes('therapeutic')) return 'therapeutic';
      if (raw.includes('hard') || raw.includes('rigid') || raw.includes('rgp')) return 'hard';
      return 'soft';
    };

    for (const order of inWindow) {
      const rawItems = Array.isArray(order.items) ? order.items : [];
      let lensCounted = false;
      for (const item of rawItems.map(normalizeDashboardItem)) {
        const name = item.name || item.model || 'Unnamed item';
        const count = Number(item.quantity) || 1;
        if (item.type === 'frame' && !String(name).toLowerCase().includes('own frame')) {
          out.frameQuantities.total += count;
          add(maps.frames, name, count);
        } else if (item.type === 'contact_lens') {
          const bucket = contactBucket(item);
          out.contactLensQuantities.total += count;
          out.contactLensQuantities[bucket] += count;
          add(maps[bucket], name, count);
        } else if (item.type === 'lens') {
          lensCounted = true;
          if (isProg(item, order)) {
            const tier = progTier(item, order);
            out.lensQuantities.progressive += count;
            out.lensQuantities[tier] += count;
            add(maps[tier], name, count);
          } else {
            const bucket = source(item.specifications || {});
            out.lensQuantities[bucket] += count;
            add(maps[bucket], name, count);
          }
        }
      }
      if (!lensCounted) {
        const rec = order.prescription?.selectedRecommendation || order.selectedRecommendation || order.rawData?.selectedRecommendation || null;
        const name = rec?.name || rec?.label;
        if (name) {
          const recItem = { specifications: rec || {} };
          const progressive = String(order.prescription?.prescriptionType || rec?.prescriptionType || '').toLowerCase().includes('progressive');
          const bucket = progressive ? progTier(recItem, order) : source(rec || {});
          if (progressive) out.lensQuantities.progressive += 1;
          out.lensQuantities[bucket] += 1;
          add(maps[bucket], name, 1);
        }
      }
    }

    out.lensDetails.intern = rows(maps.intern);
    out.lensDetails.extern = rows(maps.extern);
    out.lensDetails.rx = rows(maps.rx);
    out.lensDetails.progressivePremium = rows(maps.progressivePremium);
    out.lensDetails.progressiveFair = rows(maps.progressiveFair);
    out.lensDetails.progressiveModest = rows(maps.progressiveModest);
    out.frameDetails = rows(maps.frames);
    out.contactLensDetails.hard = rows(maps.hard);
    out.contactLensDetails.soft = rows(maps.soft);
    out.contactLensDetails.therapeutic = rows(maps.therapeutic);
    return out;
  };

  const apiQuantityTotal = (qty?.lensQuantities
    ? (qty.lensQuantities.intern + qty.lensQuantities.extern + qty.lensQuantities.rx + (qty.lensQuantities.progressive || 0))
    : 0) + (qty?.frameQuantities?.total || 0) + (qty?.contactLensQuantities?.total || 0);
  const effectiveQty = apiQuantityTotal > 0 || inWindow.length === 0 ? qty : buildFallbackQuantity();

  const sv        = effectiveQty?.lensQuantities ? (effectiveQty.lensQuantities.intern + effectiveQty.lensQuantities.extern + effectiveQty.lensQuantities.rx) : 0;
  const prog      = effectiveQty?.lensQuantities?.progressive || 0;
  const frameQty  = effectiveQty?.frameQuantities?.total || 0;
  const contactQty = effectiveQty?.contactLensQuantities?.total || 0;
  const lensQty   = sv + prog;
  const svIntern  = effectiveQty?.lensQuantities?.intern || 0;
  const svExtern  = effectiveQty?.lensQuantities?.extern || 0;
  const svRx      = effectiveQty?.lensQuantities?.rx     || 0;
  const progPremium = effectiveQty?.lensQuantities?.progressivePremium || 0;
  const progFair    = effectiveQty?.lensQuantities?.progressiveFair    || 0;
  const progModest  = effectiveQty?.lensQuantities?.progressiveModest  || 0;
  const contactHard        = effectiveQty?.contactLensQuantities?.hard        || 0;
  const contactSoft        = effectiveQty?.contactLensQuantities?.soft        || 0;
  const contactTherapeutic = effectiveQty?.contactLensQuantities?.therapeutic || 0;

  let totalDue = 0, totalPaid = 0;
  for (const o of inWindow) {
    if (o.status === 'cancelled') continue;
    const t = Number(o.total) || 0;
    const rp = Number(o.payment?.amount) || 0;
    const paid = o.payment?.status === 'paid' ? t : Math.min(rp, t);
    totalDue += t; totalPaid += paid;
  }
  const remaining = Math.max(0, totalDue - totalPaid);
  const paidPct   = totalDue > 0 ? Math.round((totalPaid / totalDue) * 100) : 0;

  let confirmedCount = 0, completedCount = 0, deliveredCount = 0;
  for (const o of inWindow) {
    const status = lifecycleStatus(o);
    if (status === 'completed') completedCount++;
    else if (status === 'delivered') deliveredCount++;
    else if (['confirmed','packaged_at_store','in_transit_to_central','at_central_inbound',
      'in_transit_to_lab','at_lab','in_production','manufacturing','quality_check',
      'returning_to_central','at_central_outbound','in_transit_to_store',
      'ready_for_pickup','out_for_delivery'].includes(status)) confirmedCount++;
  }
  const ordersTotal = confirmedCount + completedCount + deliveredCount;

  const headerSub = from && to
    ? (from === to ? new Date(from).toLocaleDateString() : `${new Date(from).toLocaleDateString()} – ${new Date(to).toLocaleDateString()}`)
    : 'all time';

  // Count-up animated totals
  const animTotalItems  = useCountUp(lensQty + frameQty + contactQty);
  const animOrdersCount = useCountUp(inWindow.length);
  const animPaidPct     = useCountUp(paidPct);
  const animOrdersTotal = useCountUp(ordersTotal);

  // ── Crumbs ───────────────────────────────────────────────────────
  const Crumbs = ({ root, path, onJump }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', flexWrap: 'wrap', fontSize: '0.78125rem', marginBottom: '0.5rem' }}>
      <button onClick={() => onJump(0)} style={{ all: 'unset', cursor: 'pointer', color: theme.colors.accent, fontWeight: 600 }}>← {root}</button>
      {path.slice(1).map((c,i) => (
        <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
          <span style={{ color: '#cbd5e1' }}>/</span>
          <button onClick={() => onJump(i+1)}
            style={{ all: 'unset', cursor: 'pointer', color: i+1===path.length-1?'#0f172a':theme.colors.accent, fontWeight: 600, textTransform: 'capitalize' }}>
            {c}
          </button>
        </span>
      ))}
    </div>
  );

  const renderItemList = (items, { keyPrefix='', emptyLabel='Nothing recorded.', accent=theme.colors.accent, onItemClick=null }={}) => {
    if (!items?.length) return <div style={{ padding:'0.75rem', color:'#94a3b8', fontStyle:'italic', textAlign:'center', fontSize:'0.8125rem' }}>{emptyLabel}</div>;
    const tot = items.reduce((s,it) => s+(it.count||0), 0) || 1;
    return (
      <div style={{ display:'flex', flexDirection:'column', gap:'0.3rem', maxHeight:230, overflowY:'auto' }}>
        {items.map((it,idx) => {
          const key = `${keyPrefix}-${idx}-${it.name}`;
          const open = !!expandedRow[key];
          const pct = Math.round(((it.count||0)/tot)*100);
          return (
            <div key={key} style={{ border:'1px solid #f1f5f9', borderRadius:8, background:'#fafcfb' }}>
              <div style={{ display:'flex', alignItems:'stretch' }}>
                <button onClick={() => onItemClick && onItemClick(it.name)}
                  style={{ all:'unset', cursor:'pointer', display:'flex', alignItems:'center', gap:'0.4rem', padding:'0.4rem 0.6rem', flex:1, fontSize:'0.8125rem' }}>
                  <span style={{ width:7, height:7, borderRadius:'50%', background:accent, flexShrink:0 }} />
                  <span style={{ color:theme.colors.accent, fontWeight:600, flex:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', textDecoration:'underline dotted', textUnderlineOffset:2 }}>{it.name}</span>
                  <span style={{ color:'#475569', fontWeight:700 }}>{it.count}</span>
                  <span style={{ color:'#94a3b8', fontSize:'0.6875rem', minWidth:28, textAlign:'right' }}>{pct}%</span>
                </button>
                <button onClick={() => setExpandedRow(m => ({...m,[key]:!open}))}
                  style={{ all:'unset', cursor:'pointer', padding:'0.4rem 0.5rem', color:'#94a3b8', fontSize:'0.75rem', display:'flex', alignItems:'center' }}>
                  {open ? '▾' : '▸'}
                </button>
              </div>
              {open && (
                <div style={{ borderTop:'1px solid #f1f5f9', background:'#f8fcfa', padding:'0.375rem 0.625rem 0.4375rem', fontSize:'0.78125rem' }}>
                  <div style={{ color:'#94a3b8', fontSize:'0.6875rem', marginBottom:3, fontWeight:600, textTransform:'uppercase', letterSpacing:'0.04em' }}>{t('manager.soldBy')}</div>
                  {!(it.sales?.length) ? <div style={{ color:'#94a3b8', fontStyle:'italic' }}>{t('manager.noData')}</div> : (
                    <div style={{ display:'flex', flexDirection:'column', gap:'0.1875rem' }}>
                      {it.sales.map((s,si) => (
                        <div key={si} style={{ display:'flex', alignItems:'center', gap:'0.375rem' }}>
                          <Users size={10} color="#94a3b8" />
                          <span style={{ color:'#334155' }}>{s.name}</span>
                          <span style={{ color:'#64748b', marginLeft:'auto', fontWeight:600 }}>{s.count}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderOrderList = (list, { emptyLabel='Nothing here.' }={}) => {
    if (!list?.length) return <div style={{ padding:'0.75rem', color:'#94a3b8', fontStyle:'italic', textAlign:'center', fontSize:'0.8125rem' }}>{emptyLabel}</div>;
    return (
      <div style={{ display:'flex', flexDirection:'column', gap:'0.3rem', maxHeight:230, overflowY:'auto' }}>
        {list.map(o => {
          const customer = o.visitor ? [o.visitor.firstName,o.visitor.lastName].filter(Boolean).join(' ')||'Customer' : 'Customer';
          const sales    = o.salesPerson ? [o.salesPerson.firstName,o.salesPerson.lastName].filter(Boolean).join(' ') : '—';
          const total    = Number(o.total)||0;
          const rawPaid  = Number(o.payment?.amount)||0;
          const paid     = o.payment?.status==='paid' ? total : Math.min(rawPaid,total);
          const due      = Math.max(0,total-paid);
          return (
            <div key={o._id} style={{ border:'1px solid #f1f5f9', borderRadius:8, padding:'0.4375rem 0.625rem', fontSize:'0.8125rem', display:'flex', flexDirection:'column', gap:2, background:'#fafcfb' }}>
              <div style={{ display:'flex', alignItems:'center', gap:'0.375rem' }}>
                <button onClick={() => onOrderClick && onOrderClick(o._id)}
                  style={{ all:'unset', cursor:'pointer', color:theme.colors.accent, fontWeight:700, fontFamily:'ui-monospace,monospace', textDecoration:'underline dotted', textUnderlineOffset:2 }}>
                  {o.orderNumber}
                </button>
                <span style={{ color:'#475569' }}>· {customer}</span>
                <span style={{ marginLeft:'auto', color:'#0f172a', fontWeight:700, fontVariantNumeric:'tabular-nums' }}>{fmtMoney(total)}</span>
              </div>
              <div style={{ display:'flex', alignItems:'center', gap:'0.375rem', color:'#94a3b8', fontSize:'0.71875rem' }}>
                <Users size={10} /> {sales}
                <span style={{ marginLeft:'auto' }}>
                  {due>0 ? <span style={{ color:'#d97706', fontWeight:600 }}>{t('manager.due')} {fmtMoney(due)}</span>
                         : <span style={{ color:'#16a34a', fontWeight:600 }}>{t('manager.paidInFull')}</span>}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const paidOrders   = inWindow.filter(o => { if(o.status==='cancelled') return false; const t=Number(o.total)||0; const rp=Number(o.payment?.amount)||0; const p=o.payment?.status==='paid'?t:Math.min(rp,t); return p>0; });
  const unpaidOrders = inWindow.filter(o => { if(o.status==='cancelled') return false; const t=Number(o.total)||0; const rp=Number(o.payment?.amount)||0; const p=o.payment?.status==='paid'?t:Math.min(rp,t); return p<t; });

  const CONF_STATUSES = ['confirmed','packaged_at_store','in_transit_to_central','at_central_inbound','in_transit_to_lab','at_lab','in_production','manufacturing','quality_check','returning_to_central','at_central_outbound','in_transit_to_store','ready_for_pickup','out_for_delivery'];
  const byBucket = {
    confirmed: inWindow.filter(o => CONF_STATUSES.includes(lifecycleStatus(o))),
    delivered: inWindow.filter(o => lifecycleStatus(o)==='delivered'),
    completed: inWindow.filter(o => lifecycleStatus(o)==='completed'),
  };

  // ── Quantity card body ────────────────────────────────────────────
  const renderQuantityBody = () => {
    const path = qtyDrill;
    if (qtyLoading && !qty) return <div style={{ padding:'1.5rem', textAlign:'center', color:'#94a3b8', fontSize:'0.8125rem' }}>{t('manager.loading')}</div>;

    if (!path.length) return (
      <DonutWrap>
        <AnimatedDonut size={144} stroke={22}
          slices={[
            { label:t('manager.simpleVision'),   value:sv,         color:theme.colors.accent },
            { label:t('manager.progressive'),     value:prog,       color:theme.colors.accentDark },
            { label:t('manager.frames'),          value:frameQty,   color:'#f59e0b' },
            { label:t('manager.contactLenses'),  value:contactQty, color:'#06b6d4' },
          ]}
          total={sv+prog+frameQty+contactQty} caption={t('manager.captionItems')}
        />
        <div className="legend">
          {[
            { id:'sv',           icon:Glasses,     label:t('manager.simpleVision'),  value:sv,         color:theme.colors.accent },
            { id:'progressive',  icon:Eye,         label:t('manager.progressive'),    value:prog,       color:theme.colors.accentDark },
            { id:'frames',       icon:PackageIcon, label:t('manager.frames'),         value:frameQty,   color:'#f59e0b' },
            { id:'contact_lens', icon:Eye,         label:t('manager.contactLenses'), value:contactQty, color:'#06b6d4' },
          ].map(row => {
            const Icon = row.icon;
            return (
              <button key={row.id} className="item"
                onClick={() => { setQtyDrill([row.id]); setExpandedRow({}); }}
                style={{ all:'unset', display:'flex', alignItems:'center', gap:'0.375rem', cursor:'pointer', padding:'0.25rem 0.375rem', borderRadius:6 }}
                onMouseEnter={e => e.currentTarget.style.background='#f0fdf4'}
                onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                <span className="swatch" style={{ background:row.color }} />
                <Icon size={12} />
                {row.label}
                <span className="num">{row.value}</span>
              </button>
            );
          })}
        </div>
      </DonutWrap>
    );

    if (path[0]==='frames') {
      if (path.length===2) {
        const fn = path[1];
        const ofs = inWindow.filter(o => (o.items||[]).some(it => (it.type||it.itemType||'').toLowerCase()==='frame' && (it.name||'').toLowerCase()===fn.toLowerCase() && !(it.name||'').toLowerCase().includes('own frame')));
        return (<div style={{display:'flex',flexDirection:'column',flex:1}}><Crumbs root={t('manager.quantity')} path={[t('manager.quantity'),t('manager.frames'),fn]} onJump={i=>setQtyDrill(i===0?[]:['frames'])} />{renderOrderList(ofs,{emptyLabel:t('manager.noOrdersForThisFrame')})}</div>);
      }
        return (<div style={{display:'flex',flexDirection:'column',flex:1}}><Crumbs root={t('manager.quantity')} path={[t('manager.quantity'),t('manager.frames')]} onJump={()=>setQtyDrill([])} />{renderItemList(effectiveQty?.frameDetails||[],{keyPrefix:'frames',emptyLabel:t('manager.noFrameSales'),accent:'#f59e0b',onItemClick:n=>{setQtyDrill(['frames',n]);setExpandedRow({}); }})}</div>);
    }

    if (path[0]==='sv') {
      if (path.length===3) {
        const [,sub,ln]=path; const lnL=ln.toLowerCase(); const ols=inWindow.filter(o=>{const hasInItems=(o.items||[]).some(it=>(it.type||it.itemType||'').toLowerCase()==='lens'&&(it.name||'').toLowerCase()===lnL);if(hasInItems)return true;const recName=(o.raw_data?.selectedRecommendation?.name||o.raw_data?.prescription?.selectedRecommendation?.name||o.lensFulfillment?.lensName||o.lens_fulfillment?.lensName||'').toLowerCase();return recName===lnL;});
        const lab={intern:t('manager.intern'),extern:t('manager.extern'),rx:t('manager.rx')};
        return (<div style={{display:'flex',flexDirection:'column',flex:1}}><Crumbs root={t('manager.quantity')} path={[t('manager.quantity'),t('manager.simpleVision'),lab[sub],ln]} onJump={i=>setQtyDrill(i===0?[]:i===1?['sv']:['sv',sub])} />{renderOrderList(ols,{emptyLabel:t('manager.noOrdersForThisLens')})}</div>);
      }
      if (path.length===2) {
        const sub=path[1]; const lab={intern:t('manager.intern'),extern:t('manager.extern'),rx:t('manager.rx')}; const col={intern:theme.colors.accent,extern:'#22c55e',rx:'#f97316'};
        return (<div style={{display:'flex',flexDirection:'column',flex:1}}><Crumbs root={t('manager.quantity')} path={[t('manager.quantity'),t('manager.simpleVision'),lab[sub]]} onJump={i=>setQtyDrill(i===0?[]:['sv'])} />{renderItemList(effectiveQty?.lensDetails?.[sub]||[],{keyPrefix:`sv-${sub}`,emptyLabel:t('manager.noLenses'),accent:col[sub],onItemClick:n=>{setQtyDrill(['sv',sub,n]);setExpandedRow({}); }})}</div>);
      }
      return (<div style={{display:'flex',flexDirection:'column',flex:1}}><Crumbs root={t('manager.quantity')} path={[t('manager.quantity'),t('manager.simpleVision')]} onJump={()=>setQtyDrill([])} />
        <DonutWrap><AnimatedDonut size={130} stroke={20} slices={[{label:t('manager.intern'),value:svIntern,color:theme.colors.accent},{label:t('manager.extern'),value:svExtern,color:'#22c55e'},{label:t('manager.rx'),value:svRx,color:'#f97316'}]} total={sv} caption={t('manager.captionSv')} />
        <div className="legend">{[{id:'intern',label:t('manager.intern'),value:svIntern,color:theme.colors.accent},{id:'extern',label:t('manager.extern'),value:svExtern,color:'#22c55e'},{id:'rx',label:t('manager.rx'),value:svRx,color:'#f97316'}].map(r=>(
          <button key={r.id} className="item" onClick={()=>{setQtyDrill(['sv',r.id]);setExpandedRow({});}}
            style={{all:'unset',display:'flex',alignItems:'center',gap:'0.375rem',cursor:'pointer',padding:'0.25rem 0.375rem',borderRadius:6}}
            onMouseEnter={e=>e.currentTarget.style.background='#f0fdf4'} onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
            <span className="swatch" style={{background:r.color}}/>{r.label}<span className="num">{r.value}</span>
          </button>))}</div>
        </DonutWrap></div>);
    }

    if (path[0]==='progressive') {
      if (path.length===3) {
        const [,sub,ln]=path; const lnL=ln.toLowerCase(); const ols=inWindow.filter(o=>{const hasInItems=(o.items||[]).some(it=>(it.type||it.itemType||'').toLowerCase()==='lens'&&(it.name||'').toLowerCase()===lnL);if(hasInItems)return true;const recName=(o.raw_data?.selectedRecommendation?.name||o.raw_data?.prescription?.selectedRecommendation?.name||o.lensFulfillment?.lensName||o.lens_fulfillment?.lensName||'').toLowerCase();return recName===lnL;});
        const lab={premium:t('manager.premium'),fair:t('manager.fair'),modest:t('manager.modest')};
        return (<div style={{display:'flex',flexDirection:'column',flex:1}}><Crumbs root={t('manager.quantity')} path={[t('manager.quantity'),t('manager.progressive'),lab[sub],ln]} onJump={i=>setQtyDrill(i===0?[]:i===1?['progressive']:['progressive',sub])} />{renderOrderList(ols,{emptyLabel:t('manager.noOrdersForThisLens')})}</div>);
      }
      if (path.length===2) {
        const sub=path[1]; const dk=sub==='premium'?'progressivePremium':sub==='modest'?'progressiveModest':'progressiveFair';
        const lab={premium:t('manager.premium'),fair:t('manager.fair'),modest:t('manager.modest')}; const col={premium:'#a855f7',fair:'#0ea5e9',modest:'#94a3b8'};
        return (<div style={{display:'flex',flexDirection:'column',flex:1}}><Crumbs root={t('manager.quantity')} path={[t('manager.quantity'),t('manager.progressive'),lab[sub]]} onJump={i=>setQtyDrill(i===0?[]:['progressive'])} />{renderItemList(effectiveQty?.lensDetails?.[dk]||[],{keyPrefix:`prog-${sub}`,emptyLabel:t('manager.noProgressives'),accent:col[sub],onItemClick:n=>{setQtyDrill(['progressive',sub,n]);setExpandedRow({}); }})}</div>);
      }
      // Tier = which slot the salesperson picked: the professional
      // recommendation → Fair, "Other (Up)" → Premium, "Other (Down)" → Modest.
      return (<div style={{display:'flex',flexDirection:'column',flex:1}}><Crumbs root={t('manager.quantity')} path={[t('manager.quantity'),t('manager.progressive')]} onJump={()=>setQtyDrill([])} />
        <DonutWrap><AnimatedDonut size={130} stroke={20} slices={[{label:t('manager.premium'),value:progPremium,color:'#a855f7'},{label:t('manager.fair'),value:progFair,color:'#0ea5e9'},{label:t('manager.modest'),value:progModest,color:'#94a3b8'}]} total={prog} caption={t('manager.captionProg')} />
        <div className="legend">{[{id:'premium',label:t('manager.premium'),value:progPremium,color:'#a855f7'},{id:'fair',label:t('manager.fair'),value:progFair,color:'#0ea5e9'},{id:'modest',label:t('manager.modest'),value:progModest,color:'#94a3b8'}].map(r=>(
          <button key={r.id} className="item" onClick={()=>{setQtyDrill(['progressive',r.id]);setExpandedRow({});}}
            style={{all:'unset',display:'flex',alignItems:'center',gap:'0.375rem',cursor:'pointer',padding:'0.25rem 0.375rem',borderRadius:6}}
            onMouseEnter={e=>e.currentTarget.style.background='#f0fdf4'} onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
            <span className="swatch" style={{background:r.color}}/>{r.label}<span className="num">{r.value}</span>
          </button>))}</div>
        </DonutWrap></div>);
    }

    if (path[0]==='contact_lens') {
      const clLab={hard:t('manager.hard'),soft:t('manager.soft'),therapeutic:t('manager.therapeutic')};
      const clCol={hard:'#0ea5e9',soft:'#10b981',therapeutic:'#8b5cf6'};
      if (path.length===3) {
        const [,sub,ln]=path;
        const ols=inWindow.filter(o=>(o.items||[]).some(it=>{const typ=String(it.type||it.itemType||'').toLowerCase();return (typ==='contact_lens'||typ==='contact lens'||typ==='contactlens'||typ==='contact')&&(it.name||'').toLowerCase()===ln.toLowerCase();}));
        return (<div style={{display:'flex',flexDirection:'column',flex:1}}><Crumbs root={t('manager.quantity')} path={[t('manager.quantity'),t('manager.contactLenses'),clLab[sub],ln]} onJump={i=>setQtyDrill(i===0?[]:i===1?['contact_lens']:['contact_lens',sub])} />{renderOrderList(ols,{emptyLabel:t('manager.noOrdersForThisContactLens')})}</div>);
      }
      if (path.length===2) {
        const sub=path[1];
        return (<div style={{display:'flex',flexDirection:'column',flex:1}}><Crumbs root={t('manager.quantity')} path={[t('manager.quantity'),t('manager.contactLenses'),clLab[sub]]} onJump={i=>setQtyDrill(i===0?[]:['contact_lens'])} />{renderItemList(effectiveQty?.contactLensDetails?.[sub]||[],{keyPrefix:`cl-${sub}`,emptyLabel:t('manager.noContactLenses'),accent:clCol[sub],onItemClick:n=>{setQtyDrill(['contact_lens',sub,n]);setExpandedRow({});}})}</div>);
      }
      return (<div style={{display:'flex',flexDirection:'column',flex:1}}><Crumbs root={t('manager.quantity')} path={[t('manager.quantity'),t('manager.contactLenses')]} onJump={()=>setQtyDrill([])} />
        <DonutWrap><AnimatedDonut size={130} stroke={20} slices={[{label:t('manager.hard'),value:contactHard,color:'#0ea5e9'},{label:t('manager.soft'),value:contactSoft,color:'#10b981'},{label:t('manager.therapeutic'),value:contactTherapeutic,color:'#8b5cf6'}]} total={contactQty} caption={t('manager.captionCl')} />
        <div className="legend">{[{id:'hard',label:t('manager.hard'),value:contactHard,color:'#0ea5e9'},{id:'soft',label:t('manager.soft'),value:contactSoft,color:'#10b981'},{id:'therapeutic',label:t('manager.therapeutic'),value:contactTherapeutic,color:'#8b5cf6'}].map(r=>(
          <button key={r.id} className="item" onClick={()=>{setQtyDrill(['contact_lens',r.id]);setExpandedRow({});}}
            style={{all:'unset',display:'flex',alignItems:'center',gap:'0.375rem',cursor:'pointer',padding:'0.25rem 0.375rem',borderRadius:6}}
            onMouseEnter={e=>e.currentTarget.style.background='#f0fdf4'} onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
            <span className="swatch" style={{background:r.color}}/>{r.label}<span className="num">{r.value}</span>
          </button>))}</div>
        </DonutWrap></div>);
    }

    return null;
  };

  const moneySlices = [{ label:t('manager.paid'), value:totalPaid, color:theme.colors.primary },{ label:t('manager.remaining'), value:remaining, color:'#f59e0b' }];
  const orderSlices = [{ label:t('manager.inPipeline'), value:confirmedCount, color:theme.colors.accent },{ label:t('manager.delivered'), value:deliveredCount, color:theme.colors.primary },{ label:t('manager.completed'), value:completedCount, color:'#0ea5e9' }];

  return (
    <div>
      <DateRangeRow>
        {['today','week','month','all'].map(p => (
          <button key={p} className={`preset ${preset===p?'active':''}`} onClick={() => applyPreset(p)}>
            {p==='today'?t('manager.today'):p==='week'?t('manager.last7Days'):p==='month'?t('manager.last30Days'):t('manager.allTime')}
          </button>
        ))}
        <span style={{ color:'#cbd5e1', margin:'0 0.125rem' }}>·</span>
        <DatePill type="date" value={from} aria-label={t('manager.startDate')}
          onChange={e => { setFrom(e.target.value); setPreset('custom'); }} />
        <span style={{ color:'#94a3b8', fontSize:'0.71875rem', fontWeight:500 }}>→</span>
        <DatePill type="date" value={to} aria-label={t('manager.endDate')}
          onChange={e => { setTo(e.target.value); setPreset('custom'); }} />
        {loading && (
          <span style={{ display:'inline-flex', alignItems:'center', gap:'0.25rem', color:'#94a3b8', fontSize:'0.71875rem' }}>
            <RefreshCw size={11} style={{ animation:'spin 1s linear infinite' }} /> {t('manager.refreshing')}
          </span>
        )}
      </DateRangeRow>

      <OverviewGrid>
        {/* ── Quantity card ── */}
        {showAnalytics && (
        <OverviewCard $index={0}>
          <AccentBar gradient={`linear-gradient(90deg, ${theme.colors.accent}, ${theme.colors.accent}cc, ${theme.colors.accentDark})`} />
          <OverviewCardHead>
            <h3><TrendingUp size={15} color={theme.colors.accent} /> {t('manager.quantitySold')}</h3>
            <span className="sub">{headerSub}</span>
          </OverviewCardHead>
          <OverviewCardBody>{renderQuantityBody()}</OverviewCardBody>
          <OverviewCardFoot>
            <strong>{animTotalItems}</strong> {t('manager.items')} <strong>{animOrdersCount}</strong> {inWindow.length===1?t('manager.orderWord'):t('manager.ordersWord')}
          </OverviewCardFoot>
        </OverviewCard>
        )}

        {/* ── Payment card ── */}
        {showPayments && (
        <OverviewCard $index={1}>
          <AccentBar gradient={`linear-gradient(90deg, ${theme.colors.primary}, ${theme.colors.primaryDark}, ${theme.colors.primary}cc)`} />
          <OverviewCardHead>
            <h3><DollarSign size={15} color={theme.colors.primary} /> {t('manager.payment')}</h3>
            <span className="sub">{headerSub}</span>
          </OverviewCardHead>
          <OverviewCardBody>
            {paymentDrill === null ? (
              <DonutWrap>
                <AnimatedDonut size={144} stroke={22} slices={moneySlices} total={`${paidPct}%`} caption={t('manager.captionPaid')} />
                <div className="legend">
                  {[{id:'paid',label:t('manager.paid'),disp:fmtMoney(totalPaid),color:theme.colors.primary},{id:'remaining',label:t('manager.remaining'),disp:fmtMoney(remaining),color:'#f59e0b'}].map(s => (
                    <button key={s.id} className="item" onClick={() => setPaymentDrill(s.id)}
                      style={{ all:'unset', display:'flex', alignItems:'center', gap:'0.375rem', cursor:'pointer', padding:'0.25rem 0.375rem', borderRadius:6 }}
                      onMouseEnter={e => e.currentTarget.style.background='#f0fdf4'}
                      onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                      <span className="swatch" style={{ background:s.color }} />{s.label}<span className="num">{s.disp}</span>
                    </button>
                  ))}
                  <div className="item" style={{ borderTop:'1px solid #f0fdf4', paddingTop:'0.3rem', marginTop:'0.15rem' }}>
                    <span className="swatch" style={{ background:'transparent' }} />
                    <strong style={{ color:'#64748b' }}>{t('manager.total')}</strong>
                    <span className="num">{fmtMoney(totalDue)}</span>
                  </div>
                </div>
              </DonutWrap>
            ) : (
              <div style={{ display:'flex', flexDirection:'column', flex:1 }}>
                <Crumbs root={t('manager.payment')} path={[t('manager.payment'), paymentDrill==='paid'?t('manager.paid'):t('manager.remaining')]} onJump={() => setPaymentDrill(null)} />
                {renderOrderList(paymentDrill==='paid'?paidOrders:unpaidOrders, { emptyLabel: paymentDrill==='paid'?t('manager.noPaymentsRecorded'):t('manager.nothingOutstanding') })}
              </div>
            )}
          </OverviewCardBody>
          <OverviewCardFoot>
            <strong>{animPaidPct}%</strong> {t('manager.collectedOf')} <strong>{fmtMoney(totalDue)}</strong> {t('manager.ofTotal')}
          </OverviewCardFoot>
        </OverviewCard>
        )}

        {/* ── Orders card ── */}
        {showAnalytics && (
        <OverviewCard $index={2}>
          <AccentBar gradient="linear-gradient(90deg, #0ea5e9, #38bdf8, #67e8f9)" />
          <OverviewCardHead>
            <h3><Truck size={15} color="#0ea5e9" /> {t('manager.ordersDelivered')}</h3>
            <span className="sub">{headerSub}</span>
          </OverviewCardHead>
          <OverviewCardBody>
            {ordersDrill === null ? (
              <DonutWrap>
                <AnimatedDonut size={144} stroke={22} slices={orderSlices} total={ordersTotal} caption={t('manager.captionOrders')} />
                <div className="legend">
                  {[
                    { id:'confirmed', label:t('manager.inPipeline'), value:confirmedCount, color:theme.colors.accent },
                    { id:'delivered', label:t('manager.delivered'),   value:deliveredCount, color:theme.colors.primary },
                    { id:'completed', label:t('manager.completed'),   value:completedCount, color:'#0ea5e9' },
                  ].map(s => (
                    <button key={s.id} className="item" onClick={() => setOrdersDrill(s.id)}
                      style={{ all:'unset', display:'flex', alignItems:'center', gap:'0.375rem', cursor:'pointer', padding:'0.25rem 0.375rem', borderRadius:6 }}
                      onMouseEnter={e => e.currentTarget.style.background='#f0fdf4'}
                      onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                      <span className="swatch" style={{ background:s.color }} />{s.label}<span className="num">{s.value}</span>
                    </button>
                  ))}
                </div>
              </DonutWrap>
            ) : (
              <div style={{ display:'flex', flexDirection:'column', flex:1 }}>
                <Crumbs root={t('manager.tabOrders')} path={[t('manager.tabOrders'), ordersDrill==='confirmed'?t('manager.inPipeline'):ordersDrill==='delivered'?t('manager.delivered'):t('manager.completed')]} onJump={() => setOrdersDrill(null)} />
                {renderOrderList(byBucket[ordersDrill]||[], { emptyLabel:t('manager.noOrdersInThisBucket') })}
              </div>
            )}
          </OverviewCardBody>
          <OverviewCardFoot>
            <strong>{animOrdersTotal}</strong> {ordersTotal===1?t('manager.activeOrder'):t('manager.activeOrders')} {t('manager.inWindow')}
          </OverviewCardFoot>
        </OverviewCard>
        )}
      </OverviewGrid>

      {/* ── Team / Availability section ── */}
      {showTeam && <TeamCard />}
    </div>
  );
};

// ─── TeamCard — employees + day-off + replacement ──────────────────
const TeamCard = ({ storeIdOverride }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // employee object being edited
  const [saving, setSaving] = useState(false);

  const storedUser = useMemo(() => {
    try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch (_) { return {}; }
  }, []);
  // Managers use their own store; an admin acting as a store falls back to the
  // store they picked (persisted in localStorage by the store picker).
  const adminActingStoreId = (() => {
    try { return JSON.parse(localStorage.getItem('adminActingStore') || 'null')?.id || null; } catch (_) { return null; }
  })();
  const storeId = storeIdOverride || storedUser?.store?.id || storedUser?.store?._id || storedUser?.storeId || adminActingStoreId;

  const fetchTeam = async () => {
    if (!storeId) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`/api/stores/${storeId}/employees`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmployees(data.employees || []);
    } catch (e) {
      console.error('fetch team failed:', e);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchTeam(); /* eslint-disable-next-line */ }, [storeId]);

  const handleSave = async (form) => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      if (form.clear) {
        await axios.delete(`/api/users/${editing.id || editing._id}/availability`, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } else {
        await axios.put(`/api/users/${editing.id || editing._id}/availability`, {
          isOff: true,
          offFrom: form.offFrom || null,
          offUntil: form.offUntil || null,
          replacementId: form.replacementId || null,
          reason: form.reason || '',
        }, { headers: { Authorization: `Bearer ${token}` } });
      }
      setEditing(null);
      await fetchTeam();
    } catch (e) {
      alert(e.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (!storeId) return null;

  return (
    <>
      <OverviewCard $index={0} style={{ marginTop: '1rem' }}>
        <AccentBar gradient="linear-gradient(90deg, #6366f1, #818cf8, #a78bfa)" />
        <OverviewCardHead>
          <h3><Users size={15} color="#6366f1" /> {t('manager.teamAvailability')}</h3>
          <span className="sub">{t('manager.employeeCount', { count: employees.length })}</span>
        </OverviewCardHead>
        <OverviewCardBody>
          {loading ? (
            <div style={{ padding: '1rem', color: '#94a3b8', fontSize: '0.85rem' }}>{t('manager.loading')}</div>
          ) : employees.length === 0 ? (
            <div style={{ padding: '1rem', color: '#94a3b8', fontSize: '0.85rem' }}>{t('manager.noEmployeesYet')}</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', padding: '0.5rem 0' }}>
              {employees.map(e => {
                const av = e.availability || {};
                const offToday = av.isOffToday;
                return (
                  <div key={e.id || e._id}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '0.625rem',
                      padding: '0.6rem 0.75rem',
                      background: offToday ? '#fef3c7' : '#f8fafc',
                      borderRadius: 8,
                      border: `1px solid ${offToday ? '#fde68a' : '#e2e8f0'}`,
                    }}>
                    <div style={{
                      width: 32, height: 32, borderRadius: '50%',
                      background: offToday ? '#f59e0b' : theme.colors.accent,
                      color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '0.75rem', fontWeight: 700, flexShrink: 0,
                    }}>
                      {(e.firstName?.[0] || '?')}{(e.lastName?.[0] || '')}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '0.875rem', fontWeight: 600, color: '#0f172a' }}>
                        {e.firstName} {e.lastName}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
                        {t(`manager.roles.${e.role}`, { defaultValue: e.role?.replace(/_/g, ' ') })} {e.employeeId ? `· ${e.employeeId}` : ''}
                      </div>
                      {offToday && (
                        <div style={{ fontSize: '0.7rem', color: '#92400e', marginTop: 2, fontWeight: 600 }}>
                          {t('manager.off')}{av.offFrom ? ` ${t('manager.offFrom', { date: av.offFrom })}` : ''}{av.offUntil ? ` ${t('manager.offUntil', { date: av.offUntil })}` : ''}
                          {av.replacementName ? ` · ${t('manager.coveredBy', { name: av.replacementName })}` : ''}
                          {av.reason ? ` · ${av.reason}` : ''}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => setEditing(e)}
                      style={{
                        all: 'unset', cursor: 'pointer',
                        padding: '0.35rem 0.7rem',
                        background: offToday ? 'white' : theme.colors.accent,
                        color: offToday ? '#0f172a' : 'white',
                        border: `1px solid ${offToday ? '#cbd5e1' : 'transparent'}`,
                        borderRadius: 6,
                        fontSize: '0.75rem', fontWeight: 600,
                      }}
                    >
                      {offToday ? t('manager.edit') : t('manager.setDayOff')}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </OverviewCardBody>
      </OverviewCard>

      {editing && (
        <AvailabilityModal
          employee={editing}
          teammates={employees.filter(x => (x.id || x._id) !== (editing.id || editing._id))}
          onClose={() => setEditing(null)}
          onSave={handleSave}
          saving={saving}
        />
      )}
    </>
  );
};

const AvailabilityModal = ({ employee, teammates, onClose, onSave, saving }) => {
  const { t } = useTranslation();
  const av = employee.availability || {};
  const today = new Date().toISOString().slice(0, 10);
  const [offFrom, setOffFrom] = useState(av.offFrom || today);
  const [offUntil, setOffUntil] = useState(av.offUntil || today);
  const [replacementId, setReplacementId] = useState(av.replacementId || '');
  const [reason, setReason] = useState(av.reason || '');

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem',
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: 'white', borderRadius: 12, padding: '1.5rem', maxWidth: 460, width: '100%',
        boxShadow: '0 20px 50px rgba(0,0,0,0.15)',
      }}>
        <h2 style={{ margin: 0, fontSize: '1.1rem', color: '#0f172a' }}>
          {t('manager.dayOff')} {employee.firstName} {employee.lastName}
        </h2>
        <p style={{ margin: '0.4rem 0 1.25rem', fontSize: '0.85rem', color: '#64748b' }}>
          {t('manager.markThisEmployeeUnavailableTheir')}
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.85rem' }}>
          <label style={{ fontSize: '0.8rem', color: '#475569' }}>
            {t('manager.from')}
            <input type="date" value={offFrom} onChange={e => setOffFrom(e.target.value)}
              style={{ width: '100%', padding: '0.45rem', border: '1px solid #cbd5e1', borderRadius: 6, marginTop: 4, fontSize: '0.85rem' }} />
          </label>
          <label style={{ fontSize: '0.8rem', color: '#475569' }}>
            {t('manager.until')}
            <input type="date" value={offUntil} onChange={e => setOffUntil(e.target.value)}
              style={{ width: '100%', padding: '0.45rem', border: '1px solid #cbd5e1', borderRadius: 6, marginTop: 4, fontSize: '0.85rem' }} />
          </label>
        </div>
        <label style={{ fontSize: '0.8rem', color: '#475569', display: 'block', marginBottom: '0.85rem' }}>
          {t('manager.replacement')}
          <select value={replacementId} onChange={e => setReplacementId(e.target.value)}
            style={{ width: '100%', padding: '0.45rem', border: '1px solid #cbd5e1', borderRadius: 6, marginTop: 4, fontSize: '0.85rem' }}>
            <option value="">{t('manager.none')}</option>
            {teammates.map(mate => (
              <option key={mate.id || mate._id} value={mate.id || mate._id}>
                {mate.firstName} {mate.lastName} ({t(`manager.roles.${mate.role}`, { defaultValue: mate.role?.replace(/_/g, ' ') })})
              </option>
            ))}
          </select>
        </label>
        <label style={{ fontSize: '0.8rem', color: '#475569', display: 'block', marginBottom: '1rem' }}>
          {t('manager.reasonOptional')}
          <input value={reason} onChange={e => setReason(e.target.value)} placeholder={t('manager.vacationSickEtc')}
            style={{ width: '100%', padding: '0.45rem', border: '1px solid #cbd5e1', borderRadius: 6, marginTop: 4, fontSize: '0.85rem', boxSizing: 'border-box' }} />
        </label>
        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
          {av.isOff && (
            <button onClick={() => onSave({ clear: true })} disabled={saving}
              style={{ all: 'unset', cursor: 'pointer', padding: '0.5rem 0.85rem', color: '#dc2626', fontWeight: 600, fontSize: '0.85rem' }}>
              {t('manager.clearDayOff')}
            </button>
          )}
          <button onClick={onClose} disabled={saving}
            style={{ all: 'unset', cursor: 'pointer', padding: '0.5rem 0.85rem', color: '#475569', fontWeight: 600, fontSize: '0.85rem' }}>
            {t('manager.cancel')}
          </button>
          <button onClick={() => onSave({ offFrom, offUntil, replacementId, reason })} disabled={saving}
            style={{ all: 'unset', cursor: 'pointer', padding: '0.5rem 0.9rem', background: '#6366f1', color: 'white', borderRadius: 6, fontWeight: 600, fontSize: '0.85rem' }}>
            {saving ? t('manager.saving') : t('manager.save')}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── PackagesTab ───────────────────────────────────────────────────
const PackagesTab = ({
  confirmedOrders, storePackages, packagesLoading,
  selectedOrderIds, toggleOrderSelected, handleCreatePackage,
  handlePackageScanSuccess, packageNotice, creatingPackage,
  receivingPackage, onReceiveDone, onCountIn,
  onRefresh, handlePrintPackage, handleThirdPartyCourier, thirdPartyBusyId,
  stockTransferRequests = [],
  onOrderDetails,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  return (
  <div style={{ display:'flex', flexDirection:'column', gap:'1rem' }}>
    {packageNotice && (
      <PackageNotice kind={packageNotice.kind}>
        <span>{packageNotice.message}</span>
        {packageNotice.pkg && (
          <button type="button" onClick={() => handlePrintPackage(packageNotice.pkg)}>
            <Printer size={13} /> {t('manager.reprintLabel')}
          </button>
        )}
      </PackageNotice>
    )}

    <Card style={{ padding:'1.25rem 1.375rem' }}>
      <h3 style={{ margin:'0 0 0.375rem', fontSize:'0.9375rem', fontWeight:700, color:'#0f172a', display:'flex', alignItems:'center', gap:'0.5rem', letterSpacing:'-0.01em' }}>
        <PackageIcon size={16} color={theme.colors.primary} /> {t('manager.bundleConfirmedOrders')}
      </h3>
      <p style={{ margin:'0 0 0.875rem', fontSize:'0.8125rem', color:'#64748b', lineHeight:1.5 }}>
        {t('manager.selectConfirmedOrdersAndTap')} <strong>{t('manager.createPackage')}</strong>{t('manager.aQrLabelPrintsAutomatically')}
      </p>
      {packagesLoading ? (
        <div style={{ padding:'1rem', color:'#94a3b8', display:'flex', alignItems:'center', gap:'0.5rem', fontSize:'0.875rem' }}>
          <RefreshCw size={15} style={{ animation:'spin 1s linear infinite' }} /> {t('manager.loading')}
        </div>
      ) : confirmedOrders.length === 0 ? (
        <div style={{ color:'#94a3b8', fontStyle:'italic', fontSize:'0.875rem' }}>{t('manager.noConfirmedOrdersWaitingTo')}</div>
      ) : (
        <>
          <div style={{ display:'flex', flexDirection:'column', gap:'0.375rem', marginBottom:'0.875rem' }}>
            {confirmedOrders.map(o => {
              const sel = selectedOrderIds.includes(o._id);
              return (
                <label key={o._id} style={{ display:'flex', alignItems:'center', gap:'0.625rem', padding:'0.5rem 0.75rem', borderRadius:10, border:`1.5px solid ${sel?theme.colors.primary:`${theme.colors.primary}1F`}`, background:sel?`linear-gradient(135deg,${theme.colors.primary}1F,${theme.colors.primary}14)`:'rgba(255,255,255,0.8)', cursor:'pointer', transition:'all 0.15s' }}>
                  <input type="checkbox" checked={sel} onChange={() => toggleOrderSelected(o._id)} style={{ accentColor:theme.colors.primary, width:15, height:15 }} />
                  <button
                    type="button"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); onOrderDetails?.(o); }}
                    style={{ all:'unset', cursor:'pointer', color:theme.colors.accent, fontWeight:800, fontVariantNumeric:'tabular-nums', textDecoration:'underline dotted', textUnderlineOffset:3 }}
                    title={t('manager.viewOrderDetails')}
                  >
                    {o.orderNumber || o.order_number || 'Order'}
                  </button>
                  <span style={{ color:'#64748b', fontSize:'0.8125rem' }}>{customerName(o.visitor)} · {fmtMoney(o.total)}</span>
                </label>
              );
            })}
          </div>
          <div style={{ display:'flex', gap:'0.5rem', alignItems:'center' }}>
            <AddButton style={{ opacity:!selectedOrderIds.length||creatingPackage?0.55:1, pointerEvents:!selectedOrderIds.length||creatingPackage?'none':'auto' }} onClick={handleCreatePackage}>
              <PackageIcon size={16} />{creatingPackage?t('manager.creating'):t('manager.bundleIntoPackage', { count: selectedOrderIds.length })}
            </AddButton>
            <button onClick={onRefresh} style={{ border:`1.5px solid ${theme.colors.primary}26`, background:`${theme.colors.primary}0D`, color:'#475569', borderRadius:9, padding:'0.5rem 0.875rem', cursor:'pointer', fontWeight:600, fontSize:'0.8125rem', display:'inline-flex', alignItems:'center', gap:'0.375rem', fontFamily:'inherit', transition:'all 0.15s' }}>
              <RefreshCw size={13} /> {t('manager.refresh')}
            </button>
          </div>
        </>
      )}
    </Card>

    <Card style={{ padding:'1.25rem 1.375rem' }}>
      <h3 style={{ margin:'0 0 0.375rem', fontSize:'0.9375rem', fontWeight:700, color:'#0f172a', display:'flex', alignItems:'center', gap:'0.5rem', letterSpacing:'-0.01em' }}>
        <ScanLine size={16} color={theme.colors.accent} /> {t('manager.receiveAReturningPackage')}
      </h3>
      <p style={{ margin:'0 0 0.875rem', fontSize:'0.8125rem', color:'#64748b', lineHeight:1.5 }}>
        {t('manager.scanTheQrWhenThe')}
      </p>
      <PackageScanner buttonLabel={t('packageScanner.scanReturningPackage')} onSuccess={handlePackageScanSuccess} />
      {receivingPackage && (
        <ReceiveChecklist
          packageId={receivingPackage._id || receivingPackage.id}
          packageNumber={receivingPackage.packageNumber}
          onDone={onReceiveDone}
        />
      )}
    </Card>

    <Card style={{ padding:'1.25rem 1.375rem' }}>
      <h3 style={{ margin:'0 0 0.875rem', fontSize:'0.9375rem', fontWeight:700, color:'#0f172a', display:'flex', alignItems:'center', gap:'0.5rem', letterSpacing:'-0.01em' }}>
        <Boxes size={16} color="#94a3b8" /> {t('manager.storeStockRequests')}
      </h3>
      {stockTransferRequests.length === 0 ? (
        <div style={{ color:'#94a3b8', fontStyle:'italic', fontSize:'0.875rem' }}>{t('manager.noStockRequestsYet')}</div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:'0.375rem' }}>
          {stockTransferRequests.slice(0,12).map(r => (
            <div key={r._id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', gap:'0.75rem', padding:'0.5625rem 0.75rem', border:`1px solid ${theme.colors.primary}14`, borderRadius:12, background:'rgba(248,255,252,0.7)' }}>
              <div>
                <strong style={{ color:'#0f172a', fontSize:'0.875rem' }}>{r.productName}</strong>
                <div style={{ color:'#94a3b8', fontSize:'0.75rem', marginTop:1 }}>
                  {tProductType(t, r.type)} {t('manager.qty2')} {r.qty}{r.sph != null ? ` · SPH ${r.sph}` : ''}{r.cyl != null ? ` / CYL ${r.cyl}` : ''}
                </div>
              </div>
              <span style={{ padding:'0.15rem 0.5rem', borderRadius:9999, fontSize:'0.6875rem', fontWeight:700, background:'linear-gradient(135deg,#eff6ff,#dbeafe)', color:'#2563eb', border:'1px solid #bfdbfe' }}>
                {tPackageStatus(t, r.status)}
              </span>
            </div>
          ))}
        </div>
      )}
    </Card>

    <Card style={{ padding:'1.25rem 1.375rem' }}>
      <h3 style={{ margin:'0 0 0.875rem', fontSize:'0.9375rem', fontWeight:700, color:'#0f172a', display:'flex', alignItems:'center', gap:'0.5rem', letterSpacing:'-0.01em' }}>
        <PackageIcon size={16} color="#94a3b8" /> {t('manager.recentPackages')}
      </h3>
      {storePackages.length === 0 ? (
        <div style={{ color:'#94a3b8', fontStyle:'italic', fontSize:'0.875rem' }}>{t('manager.noPackagesYet')}</div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:'0.375rem' }}>
          {storePackages.slice(0,12).map(p => (
            <div key={p._id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', gap:'0.5rem', padding:'0.5625rem 0.75rem', border:`1px solid ${theme.colors.primary}14`, borderRadius:12, background:'rgba(248,255,252,0.7)' }}>
              <div>
                <strong style={{ color:'#0f172a', fontSize:'0.875rem', fontVariantNumeric:'tabular-nums' }}>{p.packageNumber}</strong>
                <div style={{ color:'#94a3b8', fontSize:'0.75rem', marginTop:1 }}>
                  {p.packageType === 'stock_transfer'
                    ? t('packageLabel.stockRequestCount', { count: p.stockTransferRequests?.length || 0 })
                    : t('packageLabel.orderCount', { count: p.orders?.length || 0 })}
                  {' · '}{new Date(p.updatedAt||p.createdAt).toLocaleString()}
                </div>
              </div>
              <div style={{ display:'flex', alignItems:'center', gap:'0.4375rem' }}>
                <span style={{ padding:'0.15rem 0.5rem', borderRadius:9999, fontSize:'0.6875rem', fontWeight:700, background:p.status==='delivered_to_store'?'linear-gradient(135deg,#f0fdf4,#dcfce7)':'linear-gradient(135deg,#eff6ff,#dbeafe)', color:p.status==='delivered_to_store'?'#166534':'#2563eb', border:`1px solid ${p.status==='delivered_to_store'?'#bbf7d0':'#bfdbfe'}` }}>
                  {tPackageStatus(t, p.status)}
                </span>
                {/* A delivered transfer box still has to be counted in before
                    its lenses are stock. The checklist used to appear only in
                    the moment the scan succeeded, so if it was missed — or the
                    manifest failed to load — there was no way back to it and
                    the stock stayed in limbo. This reopens it on demand. */}
                {p.packageType === 'stock_transfer' && p.status === 'delivered_to_store' && typeof onCountIn === 'function' && (
                  <button type="button" onClick={() => onCountIn(p)} style={{ display:'inline-flex', alignItems:'center', gap:'0.25rem', background:'linear-gradient(135deg,#ecfdf5,#d1fae5)', color:'#166534', border:'1.5px solid #6ee7b7', borderRadius:8, padding:'0.25rem 0.5rem', cursor:'pointer', fontWeight:700, fontSize:'0.75rem', fontFamily:'inherit', transition:'all 0.15s' }}>
                    <Boxes size={12} /> {t('manager.countIn')}
                  </button>
                )}
                {typeof handlePrintPackage === 'function' && (
                  <button type="button" onClick={() => handlePrintPackage(p)} style={{ display:'inline-flex', alignItems:'center', gap:'0.25rem', background:`${theme.colors.primary}0D`, color:'#475569', border:`1.5px solid ${theme.colors.primary}26`, borderRadius:8, padding:'0.25rem 0.5rem', cursor:'pointer', fontWeight:600, fontSize:'0.75rem', fontFamily:'inherit', transition:'all 0.15s' }}>
                    <Printer size={12} /> {t('manager.print')}
                  </button>
                )}
                {p.status === 'packaged_at_store' && typeof handleThirdPartyCourier === 'function' && (
                  <button
                    type="button"
                    disabled={thirdPartyBusyId === p._id}
                    onClick={() => handleThirdPartyCourier(p)}
                    title={t('manager.useThisWhenTheInternal')}
                    style={{ display:'inline-flex', alignItems:'center', gap:'0.25rem', background:'linear-gradient(135deg,#f97316,#ea580c)', color:'white', border:'1.5px solid #ea580c', borderRadius:8, padding:'0.25rem 0.5rem', cursor:thirdPartyBusyId === p._id ? 'wait' : 'pointer', fontWeight:700, fontSize:'0.75rem', fontFamily:'inherit', opacity:thirdPartyBusyId === p._id ? 0.65 : 1 }}
                  >
                    <Truck size={12} /> {thirdPartyBusyId === p._id ? t('manager.handing') : t('manager.handedTo3rdPartyCourier')}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  </div>
  );
};

// ─── InventoryTab ──────────────────────────────────────────────────
const InventoryTab = ({ inventorySubTab, setInventorySubTab, handleFrameScanSuccess, hasLensStock = true }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [frameStockRefreshKey, setFrameStockRefreshKey] = useState(0);
  const [lastPulledFrame, setLastPulledFrame] = useState(null);

  // A branch that keeps no lens stock has no lens matrix to show. Move off the
  // Lenses tab rather than rendering an inventory it does not have.
  useEffect(() => {
    if (!hasLensStock && inventorySubTab === 'lenses') setInventorySubTab('frames');
  }, [hasLensStock, inventorySubTab, setInventorySubTab]);

  const handlePulledFrame = (payload) => {
    if (typeof handleFrameScanSuccess === 'function') handleFrameScanSuccess(payload);
    setLastPulledFrame(payload);
    setFrameStockRefreshKey((key) => key + 1);
  };
  return (
  <div>
    <InventorySubTabs>
      {hasLensStock && (
        <InventorySubTab active={inventorySubTab === 'lenses'} onClick={() => setInventorySubTab('lenses')}>
          <Eye size={13} /> {t('manager.lenses')}
        </InventorySubTab>
      )}
      <InventorySubTab active={inventorySubTab === 'frames'} onClick={() => setInventorySubTab('frames')}>
        <Glasses size={13} /> {t('manager.frames')}
      </InventorySubTab>
      <InventorySubTab active={inventorySubTab === 'accessories'} onClick={() => setInventorySubTab('accessories')}>
        <ShoppingBag size={13} /> {t('manager.accessories')}
      </InventorySubTab>
      <InventorySubTab active={inventorySubTab === 'consumables'} onClick={() => setInventorySubTab('consumables')}>
        <Boxes size={13} /> {t('manager.consumables')}
      </InventorySubTab>
    </InventorySubTabs>

    {inventorySubTab === 'accessories' || inventorySubTab === 'consumables' ? (
      <SupplyStockPage category={inventorySubTab === 'accessories' ? 'accessory' : 'consumable'} />
    ) : inventorySubTab === 'lenses' && hasLensStock ? (
      <>
        <InventoryCard>
          <p style={{ margin:0, fontSize:'0.8125rem', color:'#64748b', lineHeight:1.5 }}>
            {t('manager.lensStockAllocatedToThis')} <strong>{t('manager.order')}</strong> {t('manager.actionStockUpdatesOnceThe')}
          </p>
        </InventoryCard>
        <LensStockPage />
      </>
    ) : (
      <>
        <InventoryCard>
          <div style={{ display:'flex', flexDirection:'column', gap:'0.5rem' }}>
            <div style={{ fontWeight:700, color:'#0f172a', fontSize:'0.9375rem', display:'flex', alignItems:'center', gap:'0.375rem', letterSpacing:'-0.01em' }}>
              <ScanLine size={15} color={theme.colors.accent} /> {t('manager.pullAFrameFromCentral')}
            </div>
            <p style={{ margin:0, fontSize:'0.8125rem', color:'#64748b', lineHeight:1.5 }}>
              {t('manager.scanTheQrOnThe')}
            </p>
            <PackageScanner
              buttonLabel={t('packageScanner.scanFrameQr')}
              placeholder={t('manager.sku123Or24Char')}
              endpoint="/api/stock-transfers/frames/pull"
              onSuccess={handlePulledFrame}
              formatSuccess={(payload, code) =>
                `Pulled ${payload?.qty??1}× ${payload?.product?.name||code}. Store: ${payload?.storeRemaining??'?'} · Central: ${payload?.centralRemaining??'?'}.`
              }
            />
          </div>
        </InventoryCard>
        <FrameStockPage refreshKey={frameStockRefreshKey} pulledFrame={lastPulledFrame} />
      </>
    )}
  </div>
  );
};
